import React from "react";
import {
  Box,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  HtmlTooltip,
  StyledDarkRedInformationTypography,
  StyledInfoBox,
  StyledInformationSpan,
  StyledMainBox,
  StyledSwitch,
} from "../../../components/commonComponents/StyledComponents/styled";
import DateInputField from "../../../components/commonComponents/TextInputs/DateInputs";
import { BlockOutlined, Close, Info, PlayArrow, Save } from "@mui/icons-material";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { loadCampaignsForUsers, getStoresAvailableHours, getStoresDetailedAvailableHours } from "../../../api_calls/campaignRequests";
import { useDispatch, useSelector } from "react-redux";
import { loadActivStoresFromCampaign, saveClipsInformation } from "../../../api_calls/clipRequests";
import { deleteItems, updateStateData } from "../../../Store/actions";
import {
  checkStatus,
  fetchWeekNumbers,
  formatToStandardDate,
  standardFormatToday,
  isValidFileName,
  allWeekDayOptions,
  clampDate,
  checkTodayDateWithFormat,
  DetailedSelectionIconWithTooltip,
} from "../../../api_calls/utils";
import ErrorMessageText from "../../../components/commonComponents/Errors/ErrorMessageText";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import { OWNER_SELECTABLE_USER_TYPES } from "../../../components/commonComponents/Selectors/OwnerField";
import CompanyHierarchySingleSelect from "../../../components/commonComponents/Selectors/CompanyHierarchySingleSelect";
import {
  getCreateFormOwnerDefault,
  shouldShowHierarchyOwnerField,
} from "../../../components/commonComponents/Selectors/companyHierarchySelectUtils";
import { keepActiveCampaignHierarchy } from "../../userManagement/modalContent/companyPickerUtils";

import MultiSelect from "../../../components/commonComponents/Selectors/MultiSelect";
import GroupedMultiSelect from "../../../components/commonComponents/Selectors/GroupedMultiSelect";
import {
  ROOT_RETAILER_GROUP_PARAM,
  resolveLoggedInUserType,
  shouldUseRetailerStoreHierarchy,
} from "../../../components/commonComponents/Filters/storeFilterUtils";
import { canEditAcrossCompanies } from "../../../api_calls/crossCompanyAccess";
import moment from "moment-timezone";
import {
  DarkRedActionButton,
  LightGreyActionButton,
} from "../../../components/commonComponents/Buttons/Styles";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { toast } from "react-toastify";
import { checkUserDetailsFromCookie, checkUserRole } from "../../../api_calls/userManagement";
import { useDropzone } from "react-dropzone";
import StoreDetailTable from "../../../components/commonComponents/DetailedStore/StoreSelectionModal";
import { loadRetailerAccessAvailability } from "../../../api_calls/retailerSelection";
import DetailedWeekdaySelection from "./DetailedWeekdaySelection";
import DetailedHourSelection from "./DetailedHourSelection";
import TextInputField from "../../../components/commonComponents/TextInputs/TextInputField";
import { margin } from "@mui/system";
import { setBatchLoadingStatus } from "../../../api_calls/requestConfig";
import { setOverlayLoadingStatus } from "../../../api_calls/requestConfig";
import store from "../../../Store/store";

const isCompanyWithoutActiveCampaign = (company) => company?.has_active_campaign === false;
const NO_ACTIVE_CAMPAIGNS_TOOLTIP = "No active campaigns for this company";

// Stores dropdown for add/edit visual. Grouped only when the dashboard hierarchy rule matches.
const ClipStoresField = ({
  options,
  selectedOptions,
  readOnlyFlag,
  fieldError,
  onChangeCall,
  onBlurCall,
  onOpenDetail,
}) => {
  const currentStateData = useSelector((state) => state.userSelections);
  const [retailerHierarchyAvailable, setRetailerHierarchyAvailable] = React.useState(false);
  const loggedInUserType = resolveLoggedInUserType();
  const isBrandUser =
    currentStateData?.brand_report_view === true || checkUserRole()?.user_role === "Brand";
  const showRetailerHierarchy = shouldUseRetailerStoreHierarchy({
    isBrand: isBrandUser,
    retailerHierarchyAvailable,
    storeOptions: options,
  });

  React.useEffect(() => {
    let shouldApplyResult = true;

    if (loggedInUserType === "") {
      setRetailerHierarchyAvailable(false);
      return undefined;
    }

    loadRetailerAccessAvailability(loggedInUserType)
      .then((retailerAccessInfo) => {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(retailerAccessInfo.available === true);
        }
      })
      .catch(() => {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(false);
        }
      });

    return () => {
      shouldApplyResult = false;
    };
  }, [loggedInUserType]);

  const detailIcon = (setOpen) => {
    return DetailedSelectionIconWithTooltip((event) => {
      event.stopPropagation();
      setOpen(false);
      onOpenDetail();
    });
  };

  if (showRetailerHierarchy === true) {
    return (
      <GroupedMultiSelect
        label="Stores *"
        enableFullWidth={true}
        elemId={"storesDropDown"}
        readOnlyFlag={readOnlyFlag}
        options={options}
        defaultVal={selectedOptions}
        groupParam={ROOT_RETAILER_GROUP_PARAM}
        showGroupNames={true}
        showSelectionSummary={true}
        selectionCheck={(option, value) => option.label === value.label}
        onChangeCall={onChangeCall}
        onBlurCall={onBlurCall}
        fieldError={fieldError}
        additionalIconWithTooltip={true}
        additionalIconMarkup={detailIcon}
      />
    );
  }

  return (
    <MultiSelect
      label="Stores *"
      enableFullWidth={true}
      elemId={"storesDropDown"}
      readOnlyFlag={readOnlyFlag}
      options={options}
      defaultVal={selectedOptions}
      selectionCheck={(option, value) => option.label === value.label}
      onChangeCall={onChangeCall}
      onBlurCall={onBlurCall}
      fieldError={fieldError}
      additionalIconWithTooltip={true}
      additionalIconMarkup={detailIcon}
    />
  );
};

// Keep helper text out of document flow so a fieldError cannot change row
// height (flex-end would shift the input up) or the spacer between rows.
const addClipFieldErrorSx = {
  "& .MuiFormHelperText-root, & .MuiTypography-caption": {
    position: "absolute",
    left: 0,
    right: 0,
    top: "100%",
    mt: "4px !important",
    lineHeight: 1.25,
    overflowWrap: "break-word",
    pointerEvents: "none",
    zIndex: 1,
  },
};

// Extracts all store IDs from the campaign stores list regardless of nesting shape
const collectStoreIds = (storeData) => {
  const ids = new Set();
  if (!storeData) return ids;
  if (Array.isArray(storeData)) {
    storeData.forEach((s) => { if (s?.id !== undefined) ids.add(s.id); });
    return ids;
  }
  if (typeof storeData === "object") {
    Object.values(storeData).forEach((val) => {
      if (Array.isArray(val)) {
        val.forEach((s) => { if (s?.id !== undefined) ids.add(s.id); });
      } else if (val && typeof val === "object" && val.id !== undefined) {
        ids.add(val.id);
      }
    });
  }
  return ids;
};

const AddNewClip = ({ open, onClose, size, usedFor, closeModal, clipInfo, setClipInfo, cloneSource }) => {

  const isCloneMode = usedFor === "add" && typeof cloneSource !== "undefined" && cloneSource !== null && Object.keys(cloneSource).length !== 0;

  const [isDetailSelectionModalOpen, setIsDetailSelectionModalOpen] = React.useState(false);
  const [storePreselection, setStorePreselection] = React.useState([]);
  const [isDetailedWeekdayModalOpen, setIsDetailedWeekdayModalOpen] = React.useState(false);
  const [isDetailedHourModalOpen, setIsDetailedHourModalOpen] = React.useState(false);
  // the field tooltips are controlled so that they can be suppressed while the
  // detailed selection icon inside the same field shows its own tooltip
  const [hoveredDetailIcon, setHoveredDetailIcon] = React.useState(null);
  const [isWeekdayFieldTooltipOpen, setIsWeekdayFieldTooltipOpen] = React.useState(false);
  const [isHourFieldTooltipOpen, setIsHourFieldTooltipOpen] = React.useState(false);

  const currentStateData = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const campaignNameListOptions =
    typeof currentStateData.exclusive_campaign_list !== "undefined" &&
      typeof currentStateData.exclusive_campaign_list.active_campaign_name_list !== "undefined"
      ? currentStateData.exclusive_campaign_list.active_campaign_name_list
      : [];
  const campaignDatesList =
    typeof currentStateData.exclusive_campaign_list !== "undefined" &&
      typeof currentStateData.exclusive_campaign_list.campaign_dates_list !== "undefined"
      ? currentStateData.exclusive_campaign_list.campaign_dates_list
      : {};
  const datesNotSelected =
    !currentStateData.new_clip_data?.clip_start_date ||
    !currentStateData.new_clip_data?.clip_end_date;
  const mapWeekdaysToNumbers = (weekdayOptions) => {
    const weekdayMap = {
      "Sunday": 0,
      "Monday": 1,
      "Tuesday": 2,
      "Wednesday": 3,
      "Thursday": 4,
      "Friday": 5,
      "Saturday": 6,
    };

    return weekdayOptions
      .map(opt => weekdayMap[opt.label])
      .filter(num => num !== undefined);
  };
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [minSelectableDateForClip, setMinSelectableDateForClip] = React.useState(standardFormatToday());
  const [showStartDateConfirmation, setStartDateConfirmation] = React.useState(false);
  const [showEndDateConfirmation, setEndDateConfirmation] = React.useState(false);
  const [showPlayoutStopConfirmation, setShowPlayoutStopConfirmation] = React.useState(false);
  const [showContinuePlayoutConfirmation, setShowContinuePlayoutConfirmation] = React.useState(false);
  const [hoursOptions, setHoursOptions] = React.useState([]);
  const [weekdayOptions, setWeekdayOptions] = React.useState([]);
  const [committedWeeksKey, setCommittedWeeksKey] = React.useState("");
  const [committedWeekdaysKey, setCommittedWeekdaysKey] = React.useState("");
  const [weekdaysAvailabilityReady, setWeekdaysAvailabilityReady] = React.useState(false);
  const [hoursAvailabilityReady, setHoursAvailabilityReady] = React.useState(false);

  const checkUserDetails = checkUserDetailsFromCookie();
  let userDetailObj = {};
  if (checkUserDetails.success === true) {
    userDetailObj = checkUserDetails.user_detail;
  }
  const [userInfo, setUserInfo] = React.useState(userDetailObj);

  const [acceptedFiles, setAcceptedFiles] = React.useState([]);
  const [invalidFiles, setInvalidFiles] = React.useState([]);
  const [rejectedFiles, setRejectedFiles] = React.useState([]);
  const [resolutionErrors, setResolutionErrors] = React.useState([]);
  const [fileDialogActive, setFileDialogActive] = React.useState(false);

  // Tracks whether the first date field interaction in clone mode has already occurred;
  // used to trigger a one-time full scheduling reset on the initial change
  const cloneDateChangedRef = React.useRef(false);

  const prevClipStartDateRef = React.useRef(
    usedFor === "update" && clipInfo?.start_date
      ? formatToStandardDate(clipInfo.start_date)
      : (currentStateData.new_clip_data?.clip_start_date || null)
  );
  const prevClipEndDateRef = React.useRef(
    usedFor === "update" && clipInfo?.end_date
      ? formatToStandardDate(clipInfo.end_date)
      : (currentStateData.new_clip_data?.clip_end_date || null)
  );
  const lastFetchedStoreKeyRef = React.useRef("");
  const lastFetchedWeeksKeyRef = React.useRef("");
  const lastFetchedWeekdaysKeyRef = React.useRef("");
  // const maxFileSize = 50 * 1024 * 1024;
  // const minFileInBytes = 6;
  // convert bytes into MB
  /* const convertToMb = (bytesValue) => {
    return (bytesValue / (1024 * 1024)).toFixed(2);
  }; */

  // set duplicated file details to be shown under rejected list
  const checkFileNameValidation = (newFiles) => {
    let discardedFiles = [];
    const validFile = newFiles.filter((file) => {
      const isValid = isValidFileName(file.name);
      if (!isValid) {
        discardedFiles.push(file);
      }
      return isValid;
    });

    setInvalidFiles([...discardedFiles]);

    return validFile.length !== 0 ? true : false;
  };

  // Check video resolution - must be exactly 1920x1080
  const checkVideoResolution = (file) => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        const width = video.videoWidth;
        const height = video.videoHeight;

        if (width > 1920 || height > 1920) {
          resolve({ valid: false, width, height });
        } else {
          resolve({ valid: true });
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ valid: false, error: 'Resolution too high. Resolution must not exceed 1920x1080px.' });
      };

      video.src = url;
    });
  };
  // drag drop config
  const maxFileSize = 200 * 1024 * 1024;
  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept: {
      // 'image/png': ['.png'],
      // 'image/jpeg': ['.jpg', '.jpeg'],
      'video/mp4': ['.mp4'],
    },
    noDragEventsBubbling: true,
    maxSize: maxFileSize,
    onFileDialogOpen: () => {
      setFileDialogActive(true);
    },
    onFileDialogCancel: () => {
      setFileDialogActive(false); // reset when dialog is closed
    },
    // Disable drag while file dialog is open
    noDrag: fileDialogActive,
    onDrop: async (newFiles, fileRejections) => {
      setFileDialogActive(false);
      checkFileNameValidation(newFiles);

      // Validate file names first
      const validNameFiles = newFiles.filter((file) => isValidFileName(file.name));

      if (validNameFiles.length > 0) {
        // Check resolution for valid files
        const resolutionCheck = await checkVideoResolution(validNameFiles[0]);

        if (resolutionCheck.valid) {
          // Resolution is correct - accept the file
          setAcceptedFiles([validNameFiles[0]]);
          setResolutionErrors([]);
        } else {
          // Resolution is incorrect - reject the file
          const errorMsg = resolutionCheck.error
            ? resolutionCheck.error
            : 'Resolution too high. Resolution must not exceed 1920x1080px.';

          setAcceptedFiles([]);
          setResolutionErrors([{ file: validNameFiles[0], message: errorMsg }]);
        }
      } else {
        setAcceptedFiles([]);
        setResolutionErrors([]);
      }

      setRejectedFiles((prev) => [...fileRejections.map(({ file }) => file)]);
    },
  });

  const statusBasedReadOnly =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
      ["Completed"].includes(currentStateData.new_clip_data.clip_start_end_status)
      ? true
      : false;

  const hierarchyRetailers =
    typeof currentStateData.company_hierarchy !== "undefined" &&
      Array.isArray(currentStateData.company_hierarchy.retailers)
      ? currentStateData.company_hierarchy.retailers
      : [];
  const hierarchyRetailersForOwner = React.useMemo(
    () => keepActiveCampaignHierarchy(hierarchyRetailers),
    [hierarchyRetailers]
  );
  const showHierarchyOwner = shouldShowHierarchyOwnerField({
    userType: userInfo.user_type,
    usedFor,
    retailers: hierarchyRetailersForOwner,
  });

  // Update mode keeps selection on clipInfo; add and clone use local storePreselection.
  const storeSelectionInfo = usedFor === "update" ? clipInfo : storePreselection;
  const setStoreSelectionInfo = usedFor === "update" ? setClipInfo : setStorePreselection;
  const dropdownStoreOptions = currentStateData.restructured_stores_for_visuals || [];
  const modalStoreOptions = (currentStateData.stores_list_for_visuals || []).filter((storeRecord) =>
    dropdownStoreOptions.some((option) => option.id === storeRecord.id)
  );

  // One writer for the dropdown and the detail modal, so both stay on the same selection.
  const applyStoreSelection = (selectedOptions) => {
    const safeSelection = Array.isArray(selectedOptions) ? selectedOptions : [];

    setStoreSelectionInfo((prev) => ({
      ...prev,
      selected_store_options: safeSelection,
    }));

    const storeLabels = [];
    safeSelection.forEach((storeOption) => {
      if (storeOption.id === "all") {
        dropdownStoreOptions.forEach((option) => {
          if (option.id !== "all") {
            storeLabels.push(option.label);
          }
        });
      } else {
        storeLabels.push(storeOption.label);
      }
    });

    dispatchAction(
      updateStateData({
        clip_stores: storeLabels,
        selected_store_options: safeSelection,
      })
    );
  };

  const hasRestoredStoreSelection = React.useRef(false);

  React.useEffect(() => {
    if (typeof currentStateData.stores_list_for_visuals === "undefined") {
      return;
    }
    hasRestoredStoreSelection.current = false;
  }, [currentStateData.stores_list_for_visuals]);

  React.useEffect(() => {
    if (hasRestoredStoreSelection.current) {
      return;
    }
    if (!dropdownStoreOptions.length) {
      return;
    }

    // Clone writes its store list after the campaign stores arrive. Wait for that
    // write so a new-clip "select all" does not replace the copied stores.
    const previouslySelected = storeSelectionInfo?.selected_store_options || [];
    if (!previouslySelected.length) {
      if (isCloneMode === true) {
        return;
      }
      applyStoreSelection(dropdownStoreOptions);
      hasRestoredStoreSelection.current = true;
      return;
    }

    const restoredSelection = dropdownStoreOptions.filter((storeOption) =>
      previouslySelected.some((selected) => selected.id === storeOption.id)
    );
    if (restoredSelection.length) {
      applyStoreSelection(restoredSelection);
    }
    hasRestoredStoreSelection.current = true;
  }, [dropdownStoreOptions, storeSelectionInfo, isCloneMode]);

  const handleFieldChange = (clipData, overwrite = false) => {
    const selectedClipData = overwrite === true ?
      {
        clip_unlimited: false, clip_volume: 190
      } :
      typeof currentStateData.new_clip_data !== "undefined" ? currentStateData.new_clip_data : {};
    dispatchAction(
      updateStateData({
        new_clip_data: { ...selectedClipData, ...clipData },
      })
    );

    setFieldErrors((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      Object.keys(clipData).forEach((key) => {
        if (next[key]) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const clearFieldError = (errorKey) => {
    setFieldErrors((prev) => {
      if (!prev?.[errorKey]) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  };

  const calculateWeeks = (datesData) => {
    const clipStart = typeof datesData.clip_start_date !== "undefined" ? datesData.clip_start_date : "";
    const clipEnd = typeof datesData.clip_end_date !== "undefined" ? datesData.clip_end_date : "";
    let weeks = [];
    if (clipStart !== "" && clipEnd !== "") {
      weeks = fetchWeekNumbers(clipStart, clipEnd);
      // auto select all the weeks
    }
    return weeks;
  };

  // Week selection handler - sets dates based on field disabled states, clamped to campaign bounds
  const handleWeekSelect = React.useCallback((monday, sunday, fieldRole) => {
    const campaignStart = typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.campaign_start_date !== "undefined"
      ? formatToStandardDate(currentStateData.new_clip_data.campaign_start_date)
      : null;

    const campaignEnd = typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.campaign_end_date !== "undefined"
      ? formatToStandardDate(currentStateData.new_clip_data.campaign_end_date)
      : null;

    const isStartDisabled = typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
      ["Active", "Completed"].includes(currentStateData.new_clip_data.clip_start_end_status);

    const isEndDisabled = typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
      (["Completed"].includes(currentStateData.new_clip_data.clip_start_end_status) ||
        currentStateData.new_clip_data.active === false);

    if (isStartDisabled && isEndDisabled) {
      return;
    }

    // Constraints mirroring the start date field's minSelectable prop
    let startFieldMin = minSelectableDateForClip;
    if (campaignStart && campaignStart > startFieldMin) {
      startFieldMin = campaignStart;
    }
    const clampedStart = clampDate(monday, startFieldMin, campaignEnd);

    // Constraints mirroring the end date field's minSelectable IIFE:
    // if the resolved start date is today or later, end must be >= start;
    // otherwise fall back to the global minimum selectable date
    const today = standardFormatToday();
    const endFieldMin = clampedStart >= today ? clampedStart : minSelectableDateForClip;
    const clampedEnd = clampDate(sunday, endFieldMin, campaignEnd);

    if (clampedStart > clampedEnd) {
      return;
    }

    const updateData = {};

    if (fieldRole === "end") {
      // Week selected from the end date field: anchor end to Sunday, derive start from Monday
      if (!isEndDisabled) {
        updateData.clip_end_date = clampedEnd;
      }
      if (!isStartDisabled) {
        updateData.clip_start_date = clampedStart;
      }
    } else {
      // Week selected from the start date field: anchor start to Monday, derive end from Sunday
      if (!isStartDisabled) {
        updateData.clip_start_date = clampedStart;
      }
      if (!isEndDisabled) {
        updateData.clip_end_date = clampedEnd;
      }
    }

    if (Object.keys(updateData).length > 0) {
      handleFieldChange(updateData);

      calculateWeeks({
        clip_start_date: updateData.clip_start_date ??
          currentStateData.new_clip_data?.clip_start_date,
        clip_end_date: updateData.clip_end_date ??
          currentStateData.new_clip_data?.clip_end_date,
      });
    }
  }, [currentStateData.new_clip_data, minSelectableDateForClip]);

  // Global min/max for week selection styling (campaign bounds)
  const weekSelectionMin = React.useMemo(() => {
    return minSelectableDateForClip;
  }, [minSelectableDateForClip]);

  const weekSelectionMax = React.useMemo(() => {
    return typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.campaign_end_date !== "undefined"
      ? formatToStandardDate(currentStateData.new_clip_data.campaign_end_date)
      : null;
  }, [currentStateData.new_clip_data?.campaign_end_date]);

  const setClipStartDateConstraint = (campaignStartDate) => {
    const today = standardFormatToday(); //moment().tz(platformConfig.timezone).startOf("day");
    const campaignStart = formatToStandardDate(campaignStartDate);
    // if campaign start date = today or campaign start date is in past, we have to add one day to the start date
    if (today.getTime() === campaignStart.getTime() || today > campaignStart) {
      // const nextDay = new Date(today);
      // nextDay.setDate(today.getDate() + 1);
      setMinSelectableDateForClip(today);
    }

    // campaign start date is in future
    if (campaignStart > today) {
      setMinSelectableDateForClip(campaignStart);
    }
  };

  // save/update clip information
  const saveUpdpateClipInfo = async (shouldClosePopup, showSuccessMessage) => {
    const contactsValue = currentStateData.new_clip_data?.clip_contacts;
    const isUnlimited = currentStateData.new_clip_data?.clip_unlimited;

    const isEmpty =
      typeof contactsValue === "undefined" ||
      contactsValue?.toString().trim() === "";

    const isValidNumber =
      !isEmpty &&
      /^\d+$/.test(contactsValue) &&
      Number(contactsValue) >= 1;

    if (
      !currentStateData.new_clip_data?.campaign_id ||
      (!isUnlimited && (isEmpty || !isValidNumber))
    ) {
      setFieldErrors({
        ...(!currentStateData.new_clip_data?.campaign_id && {
          clip_campaign: "Please select the campaign",
        }),

        ...(!isUnlimited && isEmpty && {
          clip_contacts: "Please select the contacts",
        }),

        ...(!isUnlimited && !isEmpty && !isValidNumber && {
          clip_contacts: "Impressions must be a positive number or 1",
        }),

        ...(usedFor === "add" && acceptedFiles.length === 0 && {
          clip_file: "Please select the clip file",
        }),
      });

      toast("Please fill in valid information", { type: "error" });
      return;
    }

    setBatchLoadingStatus(true);
    try {
      if (!currentStateData.new_clip_data.clip_optimized) {
        // non-optimized path needs stores_available_hours for validation
        getStoresAvailableHours({
          storeIds: currentStateData.selected_store_options.map((s) => s.id),
          startDate: currentStateData.new_clip_data?.clip_start_date,
          endDate: currentStateData.new_clip_data?.clip_end_date,
          calenderWeeks: currentStateData.new_clip_data?.clip_weeks || [],
          weekdays: getWeekdaysInRange(
            currentStateData.new_clip_data?.clip_start_date,
            currentStateData.new_clip_data?.clip_end_date
          ).map((day) => day.label),
        });
      }

      await new Promise(resolve => setTimeout(resolve, 1000));

      saveClipsInformation(usedFor, acceptedFiles, showSuccessMessage).then((info) => {
        if (info.success === false) {
          if (info.errorMessages !== "") {
            setFieldErrors(info.errorMessages);
          }
        } else {
          const successMessage =
            shouldClosePopup === true ? "Visual stopped successfully" : "Visual continued successfully";

          if (showSuccessMessage === false) {
            toast(successMessage, { type: "success" });
          }

          if (shouldClosePopup === true) {
            closeModal();
          }
        }
      }).finally(() => setBatchLoadingStatus(false));

    } catch {
      setBatchLoadingStatus(false);
    }
  };

  function getWeekdaysInRange(startDate, endDate) {
    if (!startDate || !endDate) return [];

    startDate = formatToStandardDate(startDate);
    endDate = formatToStandardDate(endDate);

    const daysInSelectedTimeperiod = Math.round((endDate - startDate) / (1000 * 3600 * 24) + 1);

    // Check if the selected period is atleast than 7 days
    if (daysInSelectedTimeperiod <= 7) {
      const date = new Date(startDate.getTime());
      const weekDayNumbersInRange = [];
      const weekDayNamesAccordingToNumbers = [];

      while (date <= endDate) {
        weekDayNumbersInRange.push(date.getDay()); //JS standard 0 = Sunnday has to be converted
        date.setDate(date.getDate() + 1);
      }

      const weekDayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      weekDayNumbersInRange.forEach((day) => {
        weekDayNamesAccordingToNumbers.push(weekDayNames[day]);
      });

      // Filter allWeekDayOptions to only include days in the date range
      return allWeekDayOptions.filter((option) =>
        weekDayNamesAccordingToNumbers.includes(option.label)
      );
    } else {
      return allWeekDayOptions;
    }
  }

  const getCombinedWeekdayOptions = () => {
    const startDate = currentStateData.new_clip_data?.clip_start_date;
    const endDate = currentStateData.new_clip_data?.clip_end_date;

    if (!startDate || !endDate) {
      return [];
    }

    const selectedWeeks = currentStateData.new_clip_data?.clip_weeks || [];
    if (selectedWeeks.length === 0) {
      return [];
    }

    const dateRangeWeekdays = getWeekdaysInRange(startDate, endDate);
    if (dateRangeWeekdays.length === 0) {
      return [];
    }

    // Hide only by date range (< 7 days). Availability only disables a day
    // when no store is open (all cells 0/X), not when some weeks are empty.
    return dateRangeWeekdays;
  };

  const isWeekdayUnavailable = (dayOption) => {
    if (!dayOption || dayOption.value === "all") return false;
    if (
      currentStateData.stores_available_weekdays === undefined ||
      currentStateData.stores_available_weekdays === null
    ) {
      return false;
    }
    return !weekdayOptions.some((w) => w.value === dayOption.value);
  };

  const storeSelectionKey = (storeOptions) =>
    (storeOptions || [])
      .map((s) => s.id)
      .filter((id) => id !== "all" && typeof id !== "undefined")
      .slice()
      .sort((a, b) => String(a).localeCompare(String(b)))
      .join(",");

  const weeksSelectionKey = (weeks) =>
    [...(weeks || [])]
      .map(Number)
      .filter((w) => !isNaN(w))
      .sort((a, b) => a - b)
      .join(",");

  const weekdaysSelectionKey = (days) =>
    [...(days || [])].map(String).sort().join(",");

  const getAvailabilityContext = (state) => {
    const storeIds = (state.selected_store_options || [])
      .map((s) => s.id)
      .filter((id) => id !== "all" && typeof id !== "undefined");
    return {
      storeIds,
      startDate: state.new_clip_data?.clip_start_date,
      endDate: state.new_clip_data?.clip_end_date,
      calenderWeeks: state.new_clip_data?.clip_weeks || [],
      selectedDays: state.new_clip_data?.clip_week_days || [],
    };
  };

  const titleCaseDay = (s) => {
    const t = String(s || "").trim();
    if (!t) return "";
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  };

  const fillHourRange = (openedHours) => {
    const sorted = [...(openedHours || [])].filter((h) => typeof h === "number").sort((a, b) => a - b);
    if (sorted.length === 0) return [];
    const openedSet = new Set(sorted);
    const minH = sorted[0];
    const maxH = sorted[sorted.length - 1];
    const options = [];
    for (let h = minH; h <= maxH; h++) {
      const start = String(h).padStart(2, "0");
      options.push({
        value: h,
        label: `${start}:00 - ${start}:59`,
        disabled: !openedSet.has(h),
      });
    }
    return options;
  };

  // First open → last open inclusive. Hours in between with no store stay
  // in the list as unavailable. Only midnight edges are trimmed.
  const buildHoursOptionsFromDetailedResponse = (responseBody, selectedDays) => {
    const dhMap = responseBody?.detailedOpenedHours;
    if (!dhMap || typeof dhMap !== "object") return [];
    const selected = new Set((selectedDays || []).map((d) => titleCaseDay(d)));
    const openSomewhere = Array(24).fill(false);

    for (const hourKey of Object.keys(dhMap)) {
      const hourInt = Number(hourKey);
      if (!Number.isInteger(hourInt) || hourInt < 0 || hourInt > 23) continue;
      const daysObj = dhMap[hourKey];
      if (!daysObj || typeof daysObj !== "object") continue;
      for (const dayKey of Object.keys(daysObj)) {
        if (!selected.has(titleCaseDay(dayKey))) continue;
        const count = Number(daysObj[dayKey]);
        if (Number.isFinite(count) && count > 0) {
          openSomewhere[hourInt] = true;
          break;
        }
      }
    }

    let minH = -1;
    let maxH = -1;
    for (let h = 0; h < 24; h++) {
      if (openSomewhere[h]) {
        if (minH < 0) minH = h;
        maxH = h;
      }
    }
    if (minH < 0) return [];

    const options = [];
    for (let h = minH; h <= maxH; h++) {
      const start = String(h).padStart(2, "0");
      options.push({
        value: h,
        label: `${start}:00 - ${start}:59`,
        disabled: !openSomewhere[h],
      });
    }
    return options;
  };

  const fetchWeekdayAvailability = async (state) => {
    const { storeIds, startDate, endDate, calenderWeeks } = getAvailabilityContext(state);
    setWeekdaysAvailabilityReady(false);
    if (storeIds.length > 0 && startDate && endDate && calenderWeeks.length > 0) {
      await getStoresAvailableHours({ storeIds, startDate, endDate, calenderWeeks });
    }
    setWeekdaysAvailabilityReady(true);
  };

  const fetchHourAvailability = async (state) => {
    const { storeIds, startDate, endDate, calenderWeeks, selectedDays } = getAvailabilityContext(state);
    setHoursAvailabilityReady(false);
    setHoursOptions([]);
    if (storeIds.length > 0 && startDate && endDate && selectedDays.length > 0) {
      const [, detailed] = await Promise.all([
        getStoresAvailableHours({
          storeIds,
          startDate,
          endDate,
          calenderWeeks,
          weekdays: selectedDays,
        }),
        getStoresDetailedAvailableHours({
          storeIds,
          startDate,
          endDate,
          calenderWeeks,
          weekdays: selectedDays,
        }),
      ]);
      const fromDetailed = detailed?.success
        ? buildHoursOptionsFromDetailedResponse(detailed.data, selectedDays)
        : [];
      if (fromDetailed.length > 0) {
        setHoursOptions(fromDetailed);
      } else {
        const opened = store.getState().userSelections.stores_available_hours || [];
        setHoursOptions(fillHourRange(opened));
      }
    }
    setHoursAvailabilityReady(true);
  };

  const refreshAvailabilityFromStores = () => {
    const state = store.getState().userSelections;
    const storeOptions = state.selected_store_options || [];
    const key = storeSelectionKey(storeOptions);
    const prev = lastFetchedStoreKeyRef.current;

    if (key === prev) return;

    const isSubsequentChange = prev !== "";
    lastFetchedStoreKeyRef.current = key;

    if (isSubsequentChange) {
      handleFieldChange({
        clip_week_days: [],
        clip_week_day_options: [],
        hours: [],
      });
      lastFetchedWeekdaysKeyRef.current = "";
      setCommittedWeekdaysKey("");
      setHoursAvailabilityReady(false);
      setHoursOptions([]);
    }

    const { calenderWeeks } = getAvailabilityContext(state);
    if (calenderWeeks.length > 0) {
      const weeksKey = weeksSelectionKey(calenderWeeks);
      lastFetchedWeeksKeyRef.current = weeksKey;
      setCommittedWeeksKey(weeksKey);
      fetchWeekdayAvailability(state);
    } else {
      lastFetchedWeeksKeyRef.current = "";
      setCommittedWeeksKey("");
      setWeekdaysAvailabilityReady(false);
      setWeekdayOptions([]);
    }
  };

  const refreshAvailabilityFromWeeks = () => {
    const state = store.getState().userSelections;
    const key = weeksSelectionKey(state.new_clip_data?.clip_weeks);
    const prev = lastFetchedWeeksKeyRef.current;

    if (key === prev) return;

    const isSubsequentChange = prev !== "";
    lastFetchedWeeksKeyRef.current = key;
    setCommittedWeeksKey(key);

    if (isSubsequentChange) {
      handleFieldChange({
        clip_week_days: [],
        clip_week_day_options: [],
        hours: [],
      });
      lastFetchedWeekdaysKeyRef.current = "";
      setCommittedWeekdaysKey("");
      setHoursAvailabilityReady(false);
      setHoursOptions([]);
    }

    if (!key) {
      setWeekdaysAvailabilityReady(false);
      setWeekdayOptions([]);
      return;
    }

    fetchWeekdayAvailability(store.getState().userSelections);
  };

  const refreshAvailabilityFromWeekdays = () => {
    const state = store.getState().userSelections;
    const key = weekdaysSelectionKey(state.new_clip_data?.clip_week_days);
    const prev = lastFetchedWeekdaysKeyRef.current;

    if (key === prev) return;

    lastFetchedWeekdaysKeyRef.current = key;
    setCommittedWeekdaysKey(key);

    if (prev !== "") {
      handleFieldChange({ hours: [] });
    }

    if (!key) {
      setHoursAvailabilityReady(false);
      setHoursOptions([]);
      return;
    }

    fetchHourAvailability(state);
  };

  const clipDataInitializedRef = React.useRef(false);

  React.useEffect(() => {
    const initializeClipForm = async () => {
      setBatchLoadingStatus(true);

      try {
        let defaultVals = {
          clip_unlimited: false,
          clip_optimized: false,
          clip_volume: "190",
        };

        // --------------------
        // UPDATE MODE
        // --------------------
        if (usedFor === "update") {
          const availabilityContext = {
            storeIds: clipInfo.selected_store_options.map((s) => s.id),
            startDate: clipInfo.start_date,
            endDate: clipInfo.end_date,
            calenderWeeks: clipInfo.selected_weeks || [],
          };

          const initResults = await Promise.all([
            loadCampaignsForUsers(clipInfo.clip_company_id),
            loadActivStoresFromCampaign(clipInfo.campaign_id, clipInfo.clip_company_id),
            getStoresAvailableHours(availabilityContext),
            getStoresAvailableHours({
              ...availabilityContext,
              weekdays: clipInfo.selected_days,
            }),
            getStoresDetailedAvailableHours({
              ...availabilityContext,
              weekdays: clipInfo.selected_days,
            }),
          ]);
          const hoursDetailed = initResults[4];
          const fromDetailed = hoursDetailed?.success
            ? buildHoursOptionsFromDetailedResponse(hoursDetailed.data, clipInfo.selected_days)
            : [];
          if (fromDetailed.length > 0) {
            setHoursOptions(fromDetailed);
          }

          const clipStatus = checkStatus(
            clipInfo.start_date,
            clipInfo.end_date
          );

          calculateWeeks({
            clip_start_date: formatToStandardDate(clipInfo.start_date),
            clip_end_date: formatToStandardDate(clipInfo.end_date),
          });

          const exclusiveCampaigns = initResults[0];
          const campaignDates = exclusiveCampaigns?.success && exclusiveCampaigns.data?.campaign_dates_list
            ? exclusiveCampaigns.data.campaign_dates_list[clipInfo.campaign_id]
            : null;

          handleFieldChange({
            id: clipInfo.id,
            content_name: clipInfo.name,
            campaign_name: clipInfo.campaign,
            campaign_id: clipInfo.campaign_id,
            clip_start_date: formatToStandardDate(clipInfo.start_date),
            clip_end_date: formatToStandardDate(clipInfo.end_date),
            campaign_start_date: campaignDates?.campaign_start_date ?? clipInfo.campaign_start_date,
            campaign_end_date: campaignDates?.campaign_end_date ?? clipInfo.campaign_end_date,
            clip_weeks: clipInfo.selected_weeks,
            clip_week_days: clipInfo.selected_days,
            clip_week_day_options: clipInfo.selected_days
              .map((d) =>
                allWeekDayOptions.find((o) => o.label === d)
              )
              .filter(Boolean),
            clip_contacts: clipInfo.campaign_contacts,
            clip_volume: clipInfo.clip_volume,
            clip_unlimited: clipInfo.clip_unlimited,
            clip_start_end_status: clipStatus,
            active: clipInfo.active,
            hours: clipInfo.hours,
          });

          const resolvedCampaignStart = campaignDates?.campaign_start_date ?? clipInfo.campaign_start_date;
          if (resolvedCampaignStart) {
            setClipStartDateConstraint(resolvedCampaignStart);
          }

          clipDataInitializedRef.current = true;
          lastFetchedStoreKeyRef.current = storeSelectionKey(clipInfo.selected_store_options);
          lastFetchedWeeksKeyRef.current = weeksSelectionKey(clipInfo.selected_weeks);
          lastFetchedWeekdaysKeyRef.current = weekdaysSelectionKey(clipInfo.selected_days);
          setCommittedWeeksKey(weeksSelectionKey(clipInfo.selected_weeks));
          setCommittedWeekdaysKey(weekdaysSelectionKey(clipInfo.selected_days));
          setWeekdaysAvailabilityReady(true);
          setHoursAvailabilityReady(true);
        } else if (isCloneMode) {
          // ---- clone mode: pre-fill from source visual ----
          const exclusiveCampaigns = await loadCampaignsForUsers(cloneSource.clip_company_id);

          const cloneCampaignDates = exclusiveCampaigns?.success && exclusiveCampaigns.data?.campaign_dates_list
            ? exclusiveCampaigns.data.campaign_dates_list[cloneSource.campaign_id]
            : null;

          // Determine the minimum selectable start date based on campaign bounds
          const today = standardFormatToday();
          let minDate = today;
          let campaignEndDate = null;

          if (cloneCampaignDates) {
            const campaignStart = formatToStandardDate(cloneCampaignDates.campaign_start_date);
            campaignEndDate = formatToStandardDate(cloneCampaignDates.campaign_end_date);
            if (campaignStart > today) {
              minDate = campaignStart;
            }
            setClipStartDateConstraint(cloneCampaignDates.campaign_start_date);
          }

          // Scheduled → start & end; Active → end only; Completed → none
          const shouldPrefillStartDate = cloneSource.status === "Scheduled";
          const shouldPrefillEndDate =
            cloneSource.status === "Scheduled" || cloneSource.status === "Active";
          const sourceStart = formatToStandardDate(cloneSource.start_date);
          const sourceEnd = formatToStandardDate(cloneSource.end_date);

          const startDateValid =
            shouldPrefillStartDate &&
            sourceStart >= minDate &&
            (!campaignEndDate || sourceStart <= campaignEndDate);

          const endDateValid =
            shouldPrefillEndDate &&
            sourceEnd >= today &&
            (!campaignEndDate || sourceEnd <= campaignEndDate);

          const validStartDate = startDateValid ? sourceStart : null;
          const validEndDate = endDateValid ? sourceEnd : null;

          // Compute available weeks for the valid date range and filter out past weeks
          let validWeeks = [];
          if (validStartDate && validEndDate) {
            const availableWeeks = await fetchWeekNumbers(validStartDate, validEndDate);
            const availableWeekValues = new Set(availableWeeks.map((w) => w.value));
            validWeeks = (cloneSource.selected_weeks || []).filter((w) => availableWeekValues.has(w));
          }

          const sourceHasSchedulingData =
            !cloneSource.clip_unlimited &&
            ((cloneSource.selected_weeks && cloneSource.selected_weeks.length > 0) ||
              (cloneSource.selected_days && cloneSource.selected_days.length > 0) ||
              (cloneSource.hours && cloneSource.hours.length > 0));

          // Store availability has to be known before the pre-fill because it decides
          // whether the scheduling data can be copied along with the stores
          await loadActivStoresFromCampaign(cloneSource.campaign_id, cloneSource.clip_company_id);

          // Read fresh Redux state synchronously to check store availability
          const freshState = store.getState().userSelections;
          // Campaign stores now live on stores_list_for_visuals (grouped dropdown + detail modal).
          const availableStoreIds = collectStoreIds(freshState.stores_list_for_visuals);
          // Copy every source store that is still available instead of falling back
          // to the full store list as soon as one store is gone
          const availableSourceStores = (cloneSource.selected_store_options || []).filter((s) =>
            availableStoreIds.has(s.id)
          );
          const storesReduced =
            availableSourceStores.length !== (cloneSource.selected_store_options || []).length;

          // Scheduling data is only eligible for copying when source had it, dates are valid
          // and the store selection could be copied completely
          const shouldCopySchedulingData =
            sourceHasSchedulingData && validStartDate !== null && validEndDate !== null && !storesReduced;

          // Dates in the past: scheduling fields stay empty. Still turn Optimized on when
          // the source clip was a subset of its own weeks/days and the store set is intact.
          let restoreOptimized = false;
          if (!shouldCopySchedulingData && sourceHasSchedulingData && !storesReduced && availableSourceStores.length > 0) {
            const sourceWeeks = (cloneSource.selected_weeks || []).map(Number);
            const sourceDays = cloneSource.selected_days || [];
            const rangeWeeks = [];
            const weekCursor = moment(sourceStart);
            const weekEnd = moment(sourceEnd);
            while (weekCursor.isSameOrBefore(weekEnd, "day")) {
              const weekNumber = weekCursor.isoWeek();
              if (!rangeWeeks.includes(weekNumber)) rangeWeeks.push(weekNumber);
              weekCursor.add(1, "days");
            }
            const rangeDays = getWeekdaysInRange(sourceStart, sourceEnd);
            const hasAllWeeks =
              rangeWeeks.length === 0 ||
              (sourceWeeks.length >= rangeWeeks.length && rangeWeeks.every((w) => sourceWeeks.includes(w)));
            const hasAllDays =
              rangeDays.length === 0 ||
              (sourceDays.length >= rangeDays.length && rangeDays.every((d) => sourceDays.includes(d.label)));
            restoreOptimized = !(hasAllWeeks && hasAllDays);
          }

          let cloneData = {
            campaign_name: cloneSource.campaign,
            campaign_id: cloneSource.campaign_id,
            // Weeks belong to the scheduling data as soon as the source had any
            clip_weeks: sourceHasSchedulingData && !shouldCopySchedulingData ? [] : validWeeks,
            // Raw weekdays and hours; the clone_pending_filter effect filters them
            // against available options once store hours are loaded
            clip_week_days: shouldCopySchedulingData ? (cloneSource.selected_days || []) : [],
            clip_week_day_options: shouldCopySchedulingData
              ? (cloneSource.selected_days || []).map(dayLabel =>
                allWeekDayOptions.find(opt => opt.label === dayLabel)
              ).filter(opt => opt !== undefined)
              : [],
            clip_contacts: cloneSource.clip_unlimited ? "" : cloneSource.campaign_contacts,
            clip_volume: cloneSource.clip_volume,
            clip_unlimited: cloneSource.clip_unlimited,
            clip_optimized: restoreOptimized,
            // Signals the filter+derive effect to run once options are available
            clone_pending_filter: shouldCopySchedulingData,
            hours: shouldCopySchedulingData ? (cloneSource.hours || []) : [],
          };

          if (validStartDate) cloneData.clip_start_date = validStartDate;
          if (validEndDate) cloneData.clip_end_date = validEndDate;

          if (cloneCampaignDates) {
            cloneData.campaign_start_date = cloneCampaignDates.campaign_start_date;
            cloneData.campaign_end_date = cloneCampaignDates.campaign_end_date;
          }

          if (canEditAcrossCompanies(userInfo.user_type) === true) {
            cloneData.clip_owner = cloneSource.clip_company_name;
            cloneData.clip_owner_id = cloneSource.clip_company_id;
          }

          // Sync the date refs so the date-cleanup effect treats this pre-fill as unchanged
          prevClipStartDateRef.current = validStartDate;
          prevClipEndDateRef.current = validEndDate;

          handleFieldChange(cloneData);

          if (availableSourceStores.length > 0) {
            setStorePreselection({ selected_store_options: availableSourceStores });
            dispatchAction(updateStateData({
              selected_store_options: availableSourceStores,
            }));
          }

          // Copy used to load hours via the weekday-change effect. That path is gone;
          // availability now comes from the detailed-hours APIs used in update mode.
          if (shouldCopySchedulingData && availableSourceStores.length > 0 && validStartDate && validEndDate) {
            const availabilityContext = {
              storeIds: availableSourceStores.map((s) => s.id).filter((id) => id !== "all"),
              startDate: validStartDate,
              endDate: validEndDate,
              calenderWeeks: validWeeks,
            };
            const cloneDays = cloneSource.selected_days || [];
            const cloneHourResults = await Promise.all([
              getStoresAvailableHours(availabilityContext),
              cloneDays.length > 0
                ? getStoresAvailableHours({ ...availabilityContext, weekdays: cloneDays })
                : Promise.resolve(null),
              cloneDays.length > 0
                ? getStoresDetailedAvailableHours({ ...availabilityContext, weekdays: cloneDays })
                : Promise.resolve(null),
            ]);
            const hoursDetailed = cloneHourResults[2];
            const fromDetailed = hoursDetailed?.success
              ? buildHoursOptionsFromDetailedResponse(hoursDetailed.data, cloneDays)
              : [];
            if (fromDetailed.length > 0) {
              setHoursOptions(fromDetailed);
            } else if (cloneDays.length > 0) {
              const opened = store.getState().userSelections.stores_available_hours || [];
              setHoursOptions(fillHourRange(opened));
            }
          }

          lastFetchedStoreKeyRef.current = storeSelectionKey(availableSourceStores);
          lastFetchedWeeksKeyRef.current = weeksSelectionKey(cloneData.clip_weeks);
          lastFetchedWeekdaysKeyRef.current = weekdaysSelectionKey(cloneData.clip_week_days);
          if ((cloneData.clip_weeks || []).length > 0) {
            setCommittedWeeksKey(weeksSelectionKey(cloneData.clip_weeks));
          }
          if ((cloneData.clip_week_days || []).length > 0) {
            setCommittedWeekdaysKey(weekdaysSelectionKey(cloneData.clip_week_days));
          }
          if (shouldCopySchedulingData) {
            setWeekdaysAvailabilityReady(true);
            if ((cloneData.clip_week_days || []).length > 0) {
              setHoursAvailabilityReady(true);
            }
          }

          clipDataInitializedRef.current = true;
        } else {
          await loadCampaignsForUsers();

          let defaultVals = {
            clip_unlimited: false,
            clip_optimized: false,
            clip_volume: "190",
          };

          if (OWNER_SELECTABLE_USER_TYPES.includes(userInfo.user_type)) {
            const data = getCreateFormOwnerDefault(userInfo, {
              companyById: currentStateData.company_hierarchy_by_id,
              hierarchyRoot: currentStateData.company_hierarchy?.root,
              retailers: hierarchyRetailersForOwner,
              userType: userInfo.user_type,
              usedFor,
            });
            if (data !== undefined) {
              defaultVals = {
                ...defaultVals,
                clip_owner: data.label,
                clip_owner_id: data.id,
              };
              const selectedOwnerId = String(data.label).includes("Cyreen") === false ? data.id : "";
              await loadCampaignsForUsers(selectedOwnerId);
            } else {
              await loadCampaignsForUsers();
            }
          }

          handleFieldChange(defaultVals);
        }
      } finally {
        setBatchLoadingStatus(false);
      }
    };

    initializeClipForm();
  }, []);

  // Optimized is not saved in BE this Effect checks if optimized should be on when updating a Clip
  React.useEffect(() => {
    // only run for update mode
    if (usedFor !== "update") {
      return;
    }

    // skip if clip data is not loaded yet
    if (typeof currentStateData.new_clip_data === "undefined" || !currentStateData.new_clip_data?.id) {
      return;
    }

    // skip if clip_optimized was already derived (prevent re-running)
    if (typeof currentStateData.new_clip_data.clip_optimized !== "undefined") {
      return;
    }

    // skip if unlimited is enabled
    if (currentStateData.new_clip_data.clip_unlimited === true) {
      handleFieldChange({ clip_optimized: false });
      return;
    }

    // wait until hours options are loaded
    if (hoursOptions.length === 0) {
      return;
    }

    // wait until weekday options are loaded from API
    if (weekdayOptions.length === 0) {
      return;
    }

    // wait until campaign weeks are loaded
    if (typeof currentStateData.campaign_weeks === "undefined" || currentStateData.campaign_weeks.length === 0) {
      return;
    }

    // -------- check if all available options are selected (= default/not optimized) --------

    // Use actual available weekdays from API - no fallback needed since we wait for it above
    const availableWeekDays = getCombinedWeekdayOptions().filter((d) => !isWeekdayUnavailable(d));

    // If no weekdays available (dates not set or no overlap), skip derivation
    if (availableWeekDays.length === 0) {
      return;
    }
    // get selected values
    const selectedDays = currentStateData.new_clip_data.clip_week_days || [];
    const selectedHours = currentStateData.new_clip_data.hours || [];
    const selectedWeeks = currentStateData.new_clip_data.clip_weeks || [];

    // get available options (exclude "all" option)
    const availableHours = hoursOptions.filter(o => o.value !== "all" && !o.disabled).map(o => o.value);
    const availableWeeks = currentStateData.campaign_weeks.filter(w => w.value !== "all").map(w => w.value);

    // check if all available options are selected (= default behavior, not optimized)
    const hasAllDays = availableWeekDays.length === 0 ||
      (selectedDays.length >= availableWeekDays.length &&
        availableWeekDays.every(day => selectedDays.includes(day.label)));

    const hasAllHours = availableHours.length === 0 ||
      (selectedHours.length >= availableHours.length &&
        availableHours.every(hour => selectedHours.includes(hour)));

    const hasAllWeeks = availableWeeks.length === 0 ||
      (selectedWeeks.length >= availableWeeks.length &&
        availableWeeks.every(week => selectedWeeks.includes(week)));

    // clip is optimized if NOT all available options are selected
    const derivedOptimized = !(hasAllDays && hasAllHours && hasAllWeeks);

    handleFieldChange({ clip_optimized: derivedOptimized });

  }, [hoursOptions, weekdayOptions, currentStateData.campaign_weeks, currentStateData.new_clip_data?.id]);

  // -------- effect: disable optimized when all stores are deselected --------
  React.useEffect(() => {
    if (typeof currentStateData.selected_store_options === "undefined") {
      return;
    }

    if (
      currentStateData.selected_store_options.length === 0 &&
      currentStateData.new_clip_data?.clip_optimized === true
    ) {
      handleFieldChange({
        clip_optimized: false,
      });
    }
  }, [currentStateData.selected_store_options]);

  // Hours options are built in fetchHourAvailability (first open → last open,
  // midday gaps stay in the list as unavailable). Drop them if weekdays are empty.
  React.useEffect(() => {
    if (currentStateData.new_clip_data?.clone_pending_filter) {
      return;
    }
    if (!currentStateData.new_clip_data?.clip_week_days?.length) {
      setHoursOptions([]);
    }
  }, [currentStateData.new_clip_data?.clip_week_days, currentStateData.new_clip_data?.clone_pending_filter]);

  // -------- effect: process available weekdays from API --------
  React.useEffect(() => {
    if (!currentStateData.stores_available_weekdays) {
      setWeekdayOptions([]);
      return;
    }
    let availableWeekdayIndices;

    if (Array.isArray(currentStateData.stores_available_weekdays)) {
      availableWeekdayIndices = currentStateData.stores_available_weekdays;
    } else if (currentStateData.stores_available_weekdays.availableWeekdays) {
      availableWeekdayIndices = currentStateData.stores_available_weekdays.availableWeekdays;
    } else {
      availableWeekdayIndices = [];
    }

    if (!Array.isArray(availableWeekdayIndices) || availableWeekdayIndices.length === 0) {
      setWeekdayOptions([]);
      return;
    }

    // Map weekday indices to weekday options
    const weekdayIndexToName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    const tempWeekdayOptions = availableWeekdayIndices
      .map(index => {
        const dayName = weekdayIndexToName[index];
        return allWeekDayOptions.find(opt => opt.label === dayName);
      })
      .filter(opt => opt !== undefined);

    setWeekdayOptions(tempWeekdayOptions);

  }, [currentStateData.stores_available_weekdays]);

  // -------- effect: remove selected weekdays that are no longer available --------
  React.useEffect(() => {
    // Skip if dates are not set
    const clipData = currentStateData.new_clip_data;
    if (!clipData?.clip_start_date || !clipData?.clip_end_date) {
      return;
    }
    // In clone mode, defer to clone filter effect for all-or-nothing copy
    if (clipData.clone_pending_filter) {
      return;
    }

    // Reset weekdays if date changed
    const startDateChanged = (clipData.clip_start_date?.getTime?.() ?? null) !== (prevClipStartDateRef.current?.getTime?.() ?? null);
    const endDateChanged = (clipData.clip_end_date?.getTime?.() ?? null) !== (prevClipEndDateRef.current?.getTime?.() ?? null);
    const dateChanged = startDateChanged || endDateChanged;

    if (dateChanged) {
      lastFetchedWeeksKeyRef.current = "";
      lastFetchedWeekdaysKeyRef.current = "";
      setCommittedWeeksKey("");
      setCommittedWeekdaysKey("");
      setWeekdaysAvailabilityReady(false);
      setHoursAvailabilityReady(false);
      setWeekdayOptions([]);
      setHoursOptions([]);
      handleFieldChange({
        clip_weeks: [],
        clip_week_days: [],
        clip_week_day_options: [],
        hours: [],
      });

      // Update refs to current dates
      prevClipStartDateRef.current = clipData.clip_start_date;
      prevClipEndDateRef.current = clipData.clip_end_date;
      return;
    }

    // Skip if no weekdays are selected
    if (!clipData.clip_week_days || clipData.clip_week_days.length === 0) {
      return;
    }

    // Use combined options (includes date range, weeks, and API constraints)
    const availableOptions = getCombinedWeekdayOptions().filter((opt) => !isWeekdayUnavailable(opt));
    const availableDayLabels = availableOptions.map(opt => opt.label);

    const currentSelectedDays = clipData.clip_week_days || [];
    const currentSelectedDayOptions = clipData.clip_week_day_options || [];

    const validDays = currentSelectedDays.filter(day => availableDayLabels.includes(day));
    const validDayOptions = currentSelectedDayOptions.filter(opt => availableDayLabels.includes(opt.label));

    // Only update if something changed
    if (validDays.length !== currentSelectedDays.length) {
      handleFieldChange({
        clip_week_days: validDays,
        clip_week_day_options: validDayOptions,
      });
    }
  }, [
    weekdayOptions,
    currentStateData.new_clip_data?.clip_week_days,
    currentStateData.new_clip_data?.clip_start_date,
    currentStateData.new_clip_data?.clip_end_date,
  ]);

  // -------- effect: remove selected hours that are no longer available --------
  React.useEffect(() => {
    // Skip if no hours are selected or no options loaded yet
    if (!currentStateData.new_clip_data?.hours ||
      currentStateData.new_clip_data.hours.length === 0 ||
      hoursOptions.length === 0) {
      return;
    }
    // In clone mode, defer to clone filter effect for all-or-nothing copy
    if (currentStateData.new_clip_data.clone_pending_filter) {
      return;
    }

    const availableHourValues = hoursOptions.filter((opt) => !opt.disabled).map(opt => opt.value);
    const currentSelectedHours = currentStateData.new_clip_data.hours || [];

    // Filter out hours that are no longer available
    const validHours = currentSelectedHours.filter(hour => availableHourValues.includes(hour));

    // Only update if something changed
    if (validHours.length !== currentSelectedHours.length) {
      handleFieldChange({
        hours: validHours,
      });
    }
  }, [hoursOptions, currentStateData.new_clip_data?.hours]);

  const currentWeeksKey = weeksSelectionKey(currentStateData.new_clip_data?.clip_weeks);
  const currentWeekdaysKey = weekdaysSelectionKey(currentStateData.new_clip_data?.clip_week_days);
  const weeksInputConfirmed = committedWeeksKey !== "" && committedWeeksKey === currentWeeksKey;
  const weekdaysInputConfirmed = committedWeekdaysKey !== "" && committedWeekdaysKey === currentWeekdaysKey;
  const weekdaysReadyForInput = weeksInputConfirmed && weekdaysAvailabilityReady;
  const hoursReadyForInput = weekdaysInputConfirmed && hoursAvailabilityReady;
  const combinedWeekdayOptions = getCombinedWeekdayOptions();
  const weekdaysFieldLocked =
    statusBasedReadOnly ||
    datesNotSelected ||
    !weekdaysReadyForInput ||
    combinedWeekdayOptions.length < 1;
  const hoursFieldLocked =
    statusBasedReadOnly ||
    datesNotSelected ||
    !hoursReadyForInput ||
    hoursOptions.length === 0;

  // -------- effect: remove selected weeks that are no longer in the available range --------
  React.useEffect(() => {
    if (
      !currentStateData.new_clip_data?.clip_weeks ||
      currentStateData.new_clip_data.clip_weeks.length === 0
    ) {
      return;
    }

    if (
      !currentStateData.campaign_weeks ||
      currentStateData.campaign_weeks.length === 0
    ) {
      return;
    }

    const availableWeekValues = currentStateData.campaign_weeks
      .filter((w) => w.value !== "all")
      .map((w) => w.value);

    const currentSelectedWeeks =
      currentStateData.new_clip_data.clip_weeks;

    const validWeeks = currentSelectedWeeks.filter((w) =>
      availableWeekValues.includes(w)
    );

    if (validWeeks.length !== currentSelectedWeeks.length) {
      handleFieldChange({ clip_weeks: validWeeks });
    }
  }, [currentStateData.campaign_weeks]);

  // -------- effect: clone mode deferred filtering and optimized derivation --------
  // Runs once all scheduling options are available; applies all-or-nothing copy for
  // weekdays/hours per spec and derives the optimized flag
  React.useEffect(() => {
    if (!isCloneMode) return;

    const data = currentStateData.new_clip_data;
    if (!data) return;
    if (!data.clone_pending_filter) return;

    if (!currentStateData.campaign_weeks || currentStateData.campaign_weeks.length === 0) {
      return;
    }

    if (!weekdaysAvailabilityReady) {
      return;
    }

    if (
      currentStateData.stores_available_weekdays === undefined ||
      currentStateData.stores_available_weekdays === null
    ) {
      return;
    }

    let availableWeekdayIndices;
    if (Array.isArray(currentStateData.stores_available_weekdays)) {
      availableWeekdayIndices = currentStateData.stores_available_weekdays;
    } else if (currentStateData.stores_available_weekdays.availableWeekdays) {
      availableWeekdayIndices = currentStateData.stores_available_weekdays.availableWeekdays;
    } else {
      availableWeekdayIndices = [];
    }
    if (!Array.isArray(availableWeekdayIndices)) {
      availableWeekdayIndices = [];
    }

    // API returned open days, but the weekdayOptions effect has not mapped them yet
    if (availableWeekdayIndices.length > 0 && weekdayOptions.length === 0) {
      return;
    }

    const dateRangeWeekDays = getCombinedWeekdayOptions().filter((d) => d.value !== "all");
    if (dateRangeWeekDays.length === 0) {
      return;
    }
    const availableWeekDays = dateRangeWeekDays.filter((d) => !isWeekdayUnavailable(d));

    // Weekdays: all-or-nothing copy
    const sourceWeekDays = data.clip_week_days || [];
    const availableDayLabels = availableWeekDays.map((d) => d.label);
    const allDaysAvailable =
      sourceWeekDays.length > 0 &&
      sourceWeekDays.every((d) => availableDayLabels.includes(d));
    const filteredDays = allDaysAvailable ? sourceWeekDays : [];
    const filteredDayOptions = allDaysAvailable
      ? availableWeekDays.filter((d) => filteredDays.includes(d.label))
      : [];
    const weekdayCloneError = !allDaysAvailable && sourceWeekDays.length > 0;

    // Hours: all-or-nothing copy
    const sourceHours = data.hours || [];
    const availableHourValues = hoursOptions.filter((o) => o.value !== "all" && !o.disabled).map((o) => o.value);

    // Guard against false negatives when hours options have not loaded yet.
    // Empty options after the fetch finished means none are available.
    if (sourceHours.length > 0 && availableHourValues.length === 0 && !hoursAvailabilityReady) {
      return;
    }

    const allHoursAvailable = sourceHours.every((h) => availableHourValues.includes(h));
    const filteredHours = allHoursAvailable ? sourceHours : [];
    const hoursCloneError = !allHoursAvailable && sourceHours.length > 0;

    // Derive optimized from the filtered values
    const selectedWeeks = data.clip_weeks || [];
    const availableWeeks = currentStateData.campaign_weeks
      .filter((w) => w.value !== "all")
      .map((w) => w.value);

    const hasAllDays =
      availableWeekDays.length === 0 ||
      (filteredDays.length >= availableWeekDays.length &&
        availableWeekDays.every((day) => filteredDays.includes(day.label)));

    const hasAllHours =
      availableHourValues.length === 0 ||
      (filteredHours.length >= availableHourValues.length &&
        availableHourValues.every((hour) => filteredHours.includes(hour)));

    const hasAllWeeks =
      availableWeeks.length === 0 ||
      (selectedWeeks.length >= availableWeeks.length &&
        availableWeeks.every((week) => selectedWeeks.includes(week)));

    const derivedOptimized = !(hasAllDays && hasAllHours && hasAllWeeks);

    // handleFieldChange clears matching fieldErrors; apply clone warnings afterwards
    handleFieldChange({
      clip_week_days: filteredDays,
      clip_week_day_options: filteredDayOptions,
      hours: filteredHours,
      clip_optimized: derivedOptimized,
      clone_pending_filter: undefined,
    });
    if (weekdayCloneError) {
      setFieldErrors((prev) => ({ ...prev, clip_week_days: "Not all selected weekdays are available anymore" }));
    }
    if (hoursCloneError) {
      setFieldErrors((prev) => ({ ...prev, hours: "Not all selected hours are available anymore" }));
    }
  }, [
    hoursOptions,
    weekdayOptions,
    weekdaysAvailabilityReady,
    hoursAvailabilityReady,
    currentStateData.new_clip_data?.clone_pending_filter,
    currentStateData.campaign_weeks,
    currentStateData.stores_available_weekdays,
  ]);

  // StoreHierarchy reads the selection once when it mounts, which can happen before the
  // clone pre-fill has landed. Until then the source stores of the copied visual stand in;
  // the ones that are no longer available are dropped by preSelectionOfStores.
  const storeSelectionForHierarchy =
    isCloneMode && typeof storePreselection.selected_store_options === "undefined"
      ? { selected_store_options: cloneSource.selected_store_options || [] }
      : storePreselection;

  const weekdayFieldTooltipTitle = datesNotSelected
    ? "Select dates to enable this option"
    : !weekdaysReadyForInput
      ? "Select calendar weeks first to enable weekday selection"
      : combinedWeekdayOptions.length === 0
        ? "No available weekdays found. The selected stores are not open during the campaign period."
        : "";
  const hourFieldTooltipTitle =
    hoursFieldLocked && !statusBasedReadOnly
      ? "Select weekdays first to enable hours selection"
      : "";

  const renderDetailSelectionIcon = (iconKey, onIconClick) => (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center" }}
      onMouseEnter={() => setHoveredDetailIcon(iconKey)}
      onMouseLeave={() => setHoveredDetailIcon(null)}
      onFocus={() => setHoveredDetailIcon(iconKey)}
      onBlur={() => setHoveredDetailIcon(null)}
    >
      {DetailedSelectionIconWithTooltip(onIconClick)}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => {
          e.preventDefault();
        }}
        fullWidth={true}
        maxWidth={size}
      >
        <DialogTitle>{usedFor === "update" ? "Edit : " + clipInfo?.name : "Add Contact-based Visual"} </DialogTitle>
        <DialogContent>
          <StyledMainBox>
            <Box sx={addClipFieldErrorSx}>
              {/* ------- file upload (only visible for "Add Clip" or If Updating a scheduled clip) -------- */}
              {usedFor === "add" ||
                (typeof currentStateData.new_clip_data !== "undefined" &&
                  typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
                  ["Scheduled"].includes(currentStateData.new_clip_data.clip_start_end_status)) ? (
                <Stack direction="row" spacing={2}>
                  <Box
                    {...getRootProps({ className: "dropzone" })}
                    height={60}
                    width="100%"
                    sx={{
                      border: "2px dashed #888",
                      padding: "20px",
                      textAlign: "center",
                      borderRadius: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <input {...getInputProps()} />
                    <Box mt={1}>
                      <Typography variant="body3" sx={{ margin: "20px" }}>
                        Drag & Drop to Upload File or Click to Browse
                      </Typography>
                      <Box height={4} />
                      <Typography variant="body2">
                        <em>.mp4 (max. 1920x1080px)</em>
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              ) : typeof currentStateData.new_clip_data !== "undefined" &&
                typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
                ["Scheduled"].includes(currentStateData.new_clip_data.clip_start_end_status) ? (
                <Stack direction="row" spacing={2}>
                  <Box
                    {...getRootProps({ className: "dropzone" })}
                    height={60}
                    width="100%"
                    sx={{
                      border: "2px dashed #888",
                      padding: "20px",
                      textAlign: "center",
                      borderRadius: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <input {...getInputProps()} />
                    <Box mt={1}>
                      <Typography variant="body3" sx={{ margin: "20px" }}>
                        Drag & Drop to Upload File or Click to Browse
                      </Typography>
                      <Box height={4} />
                      <Typography variant="body2">
                        <em>(only .mp4)</em>
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              ) : typeof currentStateData.new_clip_data !== "undefined" &&
                typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
                ["Active"].includes(currentStateData.new_clip_data.clip_start_end_status) ? (
                <>
                  {
                    currentStateData.new_clip_data.active === true ? (<DarkRedActionButton
                      component="label"
                      variant="contained"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPlayoutStopConfirmation(true);
                      }}
                      startIcon={<BlockOutlined />}
                    >
                      Stop Playout
                    </DarkRedActionButton>) : (<DarkRedActionButton
                      component="label"
                      variant="contained"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowContinuePlayoutConfirmation(true);
                      }}
                      startIcon={<PlayArrow />}
                    >
                      Continue Playout
                    </DarkRedActionButton>)
                  }
                </>
              ) : (
                <LightGreyActionButton component="label" variant="contained" startIcon={<BlockOutlined />}>
                  Stop Playout
                </LightGreyActionButton>
              )}
              {/* ------- error message ------ */}
              {typeof fieldErrors.clip_file !== "undefined" && fieldErrors.clip_file !== "" && (
                <ErrorMessageText message={fieldErrors.clip_file} />
              )}
              {/* Accepted Files. 15px matches the gap above the first inputs. */}
              {acceptedFiles.length > 0 && (
                <Box sx={{ mt: "15px" }}>
                  <h4 style={{ margin: 0 }}>Accepted File</h4>
                  <ul style={{ margin: 0 }}>
                    {acceptedFiles.map((file, index) => (
                      <li key={`${file.name}-${index}`} style={{ marginTop: "5px" }}>
                        <Stack direction="row" spacing={2}>
                          <Typography variant="body2">
                            {file.name}
                          </Typography>
                        </Stack>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}

              {/* Rejected Files. Same 15px gap as the accepted list and the first inputs. */}
              {(rejectedFiles.length > 0 || invalidFiles.length !== 0 || resolutionErrors.length > 0) && (
                <Box sx={{ mt: "15px" }}>
                  <h4 style={{ margin: 0 }}>Rejected File</h4>
                  <ul style={{ margin: 0 }}>
                    {rejectedFiles.map((file, index) => (
                      <li key={`${file.name}-${index}`} style={{ marginTop: "5px" }}>
                        <Stack direction={"row"} spacing={2}>
                          <Box width={350} sx={{ wordWrap: "break-word" }}>
                            <Typography variant="body2">
                              {file.name}
                            </Typography>
                          </Box>
                          <Box width={450}>
                            <StyledDarkRedInformationTypography variant="body2">
                              :{" "}{
                                file.size > maxFileSize ? "File size exceeds the limit of 200 MB" : "Invalid file type. Allowed types are: .mp4"
                              }
                            </StyledDarkRedInformationTypography>
                          </Box>

                        </Stack>
                      </li>
                    ))}

                    {/* duplicated files */}
                    {invalidFiles.map((file, index) => (
                      <li key={`${file.name}-${index}`} style={{ marginTop: "5px" }}>
                        <Stack direction={"row"} spacing={2}>
                          <Box width={350} sx={{ wordWrap: "break-word" }}>
                            <Typography variant="body2">
                              {file.name}
                            </Typography>
                          </Box>
                          <Box width={450}>
                            <StyledDarkRedInformationTypography variant="body2">
                              :{" "}{"Invalid file name. The file name should only contain a-z, A-Z, 0-9, -, or _"}
                            </StyledDarkRedInformationTypography>
                          </Box>

                        </Stack>
                      </li>
                    ))}

                    {/* Resolution errors */}
                    {resolutionErrors.map((item, index) => (
                      <li key={`${item.file.name}-${index}`} style={{ marginTop: "5px" }}>
                        <Stack direction={"row"} spacing={2}>
                          <Box width={350} sx={{ wordWrap: "break-word" }}>
                            <Typography variant="body2">
                              {item.file.name}
                            </Typography>
                          </Box>
                          <Box width={450}>
                            <StyledDarkRedInformationTypography variant="body2">
                              : {item.message}
                            </StyledDarkRedInformationTypography>
                          </Box>

                        </Stack>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}

              {/* 30px when the drop zone sits on the fields. 15px once a file list is between them. */}
              <Box
                height={
                  acceptedFiles.length > 0 ||
                    rejectedFiles.length > 0 ||
                    invalidFiles.length !== 0 ||
                    resolutionErrors.length > 0
                    ? 15
                    : 30
                }
              />
              {/* -------- campaign selection ---------- */}
              <Stack direction={"row"} spacing={4}>
                {/* ---------- owner selection -------- */}
                {/* Hierarchy owner (cyreen/tenant create, when more than one company exists). */}
                {showHierarchyOwner === true && (
                  <CompanyHierarchySingleSelect
                    label="Owner *"
                    retailers={hierarchyRetailersForOwner}
                    disabledOptions={isCompanyWithoutActiveCampaign}
                    tooltipTitleForDisabledOptions={NO_ACTIVE_CAMPAIGNS_TOOLTIP}
                    value={
                      typeof currentStateData.new_clip_data !== "undefined" &&
                        typeof currentStateData.new_clip_data.clip_owner !== "undefined"
                        ? {
                          label: currentStateData.new_clip_data.clip_owner,
                          id: currentStateData.new_clip_data.clip_owner_id,
                        }
                        : null
                    }
                    fieldError={
                      typeof fieldErrors.clip_owner !== "undefined" && fieldErrors.clip_owner !== ""
                        ? fieldErrors.clip_owner
                        : ""
                    }
                    onChange={(selectedOption) => {
                      if (selectedOption === null) {
                        handleFieldChange({
                          clip_owner: "",
                          clip_owner_id: "",
                        }, true);
                        return;
                      }
                      dispatchAction(deleteItems([
                        "new_clip_data",
                        "selected_store_options",
                        "stores_list_for_visuals",
                        "restructured_stores_for_visuals",
                        "filtered_regions",
                        "stores_detail",
                      ]));
                      handleFieldChange({
                        clip_owner: selectedOption.label,
                        clip_owner_id: selectedOption.id,
                      }, true);
                      const selectedOwnerId = String(selectedOption.label).includes("Cyreen") === false
                        ? selectedOption.id
                        : "";
                      loadCampaignsForUsers(selectedOwnerId);
                    }}
                  />
                )}
                {/* -------- Select Campaign ------- */}
                <SingleSelect
                  label="Campaign *"
                  elemId={"clipsCampaignList"}
                  enableFullWidth={true}
                  options={campaignNameListOptions}
                  defaultVal={
                    typeof currentStateData.new_clip_data !== "undefined" &&
                      typeof currentStateData.new_clip_data.campaign_name !== "undefined"
                      ? {
                        id: currentStateData.new_clip_data.campaign_id,
                        label: currentStateData.new_clip_data.campaign_name,
                      }
                      : ""
                  }
                  selectionCheck={(option, value) => option.id === value.id}
                  onChangeCall={(selectedOption) => {
                    // Dates come from the same exclusive list as the dropdown options.
                    const selectedCampaignDates = campaignDatesList?.[selectedOption.id] || null;

                    const campaignChanged =
                      currentStateData.new_clip_data?.campaign_id !== selectedOption.id;

                    let selectedData = {
                      campaign_id: selectedOption.id,
                      campaign_name: selectedOption.label,
                      campaign_start_date: selectedCampaignDates?.campaign_start_date,
                      campaign_end_date: selectedCampaignDates?.campaign_end_date,
                    };

                    // Switching to a different campaign invalidates the clip dates and
                    // everything derived from them (weeks/weekdays/hours/optimized), so
                    // reset them - the new campaign defines a different valid date window.
                    if (campaignChanged) {
                      selectedData = {
                        ...selectedData,
                        clip_start_date: undefined,
                        clip_end_date: undefined,
                        clip_weeks: [],
                        clip_week_days: [],
                        clip_week_day_options: [],
                        hours: [],
                        clip_optimized: false,
                        clone_pending_filter: false,
                      };
                    }

                    handleFieldChange(selectedData);

                    if (campaignChanged) {
                      // Keep the date-cleanup effect and the week options in sync with
                      // the cleared clip dates.
                      prevClipStartDateRef.current = null;
                      prevClipEndDateRef.current = null;
                      dispatchAction(updateStateData({ campaign_weeks: [] }));
                    }

                    if (selectedCampaignDates?.campaign_start_date) {
                      setClipStartDateConstraint(selectedCampaignDates.campaign_start_date);
                    }

                    // loadActivStoresFromCampaign fires the active-list and listing-check
                    // requests back to back; each toggles the per-request loader, so the
                    // backdrop flickers between them. Hold a single overlay-loading window so
                    // the loader stays steady. We use overlayLoading (not batchLoading) on
                    // purpose: the background listing tables bind their own loading state to
                    // batchLoading and would blank their rows to "Loading…" - overlayLoading
                    // is only read by the global Backdrop. Always release it in finally so a
                    // failed, aborted or rejected request can never leave a stuck loader.
                    setOverlayLoadingStatus(true);
                    (async () => {
                      try {
                        await loadActivStoresFromCampaign(selectedOption.id);
                      } catch {
                        // Errors are surfaced via toast inside the request layer; swallow
                        // here so the finally below still clears the loading state.
                      } finally {
                        setOverlayLoadingStatus(false);
                      }
                    })();
                  }}
                  readOnlyFlag={
                    typeof currentStateData.new_clip_data !== "undefined" &&
                      typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
                      ["Active", "Completed"].includes(currentStateData.new_clip_data.clip_start_end_status)
                      ? true
                      : false
                  }
                  fieldError={
                    typeof fieldErrors.clip_campaign !== "undefined" && fieldErrors.clip_campaign !== ""
                      ? fieldErrors.clip_campaign
                      : ""
                  }
                />
              </Stack>
              {/* ------- start/end date -------- */}

              {typeof currentStateData.new_clip_data !== "undefined" &&
                typeof currentStateData.new_clip_data.campaign_name !== "undefined" && (
                  <>
                    <Box height={30} />
                    <Stack direction={"row"} spacing={4}>
                      {/* ------ clip start date -------- */}
                      <DateInputField
                        fieldRequired={true}
                        elemId="startDate"
                        className="start_date_class"
                        label="Start Date "
                        fieldName="start_date"
                        minSelectable={minSelectableDateForClip}
                        maxSelectable={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                          typeof currentStateData.new_clip_data.campaign_end_date !== "undefined" &&
                          formatToStandardDate(currentStateData.new_clip_data.campaign_end_date)
                        }
                        defaultVal={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                            typeof currentStateData.new_clip_data.clip_start_date !== "undefined"
                            ? currentStateData.new_clip_data.clip_start_date
                            : ""
                        }
                        showlabel={false}
                        disabled={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                            typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
                            ["Active", "Completed"].includes(currentStateData.new_clip_data.clip_start_end_status)
                            ? true
                            : false
                        }
                        fieldError={
                          typeof fieldErrors.clip_start_date !== "undefined" && fieldErrors.clip_start_date !== ""
                            ? fieldErrors.clip_start_date
                            : ""
                        }
                        onChangeCall={(selectedDate) => {
                          if (currentStateData?.new_clip_data?.clip_unlimited === false && usedFor === "update") {
                            setEndDateConfirmation(true);
                          }
                          setStartDateConfirmation(moment(selectedDate).isSame(standardFormatToday()));

                          const selectedData = {
                            clip_start_date: selectedDate,
                          };

                          // On the first date interaction in clone mode, clear all scheduling fields
                          if (isCloneMode && !cloneDateChangedRef.current) {
                            cloneDateChangedRef.current = true;
                            selectedData.clip_weeks = [];
                            selectedData.clip_week_days = [];
                            selectedData.clip_week_day_options = [];
                            selectedData.hours = [];
                          }

                          handleFieldChange(selectedData);
                          calculateWeeks({
                            ...currentStateData.new_clip_data,
                            clip_start_date: selectedDate,
                          });
                        }}
                        onWeekSelect={handleWeekSelect}
                        weekSelectionMinDate={weekSelectionMin}
                        weekSelectionMaxDate={weekSelectionMax}
                      />

                      {/* -------- clip end date ------- */}
                      <DateInputField
                        fieldRequired={true}
                        elemId="endDate"
                        label="End Date "
                        className="end_date_class"
                        fieldName="end_date"
                        defaultVal={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                            typeof currentStateData.new_clip_data.clip_end_date !== "undefined"
                            ? currentStateData.new_clip_data.clip_end_date
                            : ""
                        }
                        showlabel={false}
                        disabled={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                            typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
                            (["Completed"].includes(currentStateData.new_clip_data.clip_start_end_status) || currentStateData.new_clip_data.active === false)
                            ? true
                            : false
                        }
                        minSelectable={(() => {
                          if (
                            currentStateData.new_clip_data &&
                            currentStateData.new_clip_data.clip_start_date &&
                            moment(currentStateData.new_clip_data.clip_start_date).isSameOrAfter(moment(), "day")
                          ) {
                            return currentStateData.new_clip_data.clip_start_date;
                          } else if (
                            currentStateData.new_clip_data &&
                            currentStateData.new_clip_data.clip_start_date &&
                            moment(currentStateData.new_clip_data.clip_start_date).isSameOrBefore(moment(), "day")
                          ) {
                            return minSelectableDateForClip;
                          } else if (
                            typeof currentStateData.new_clip_data !== "undefined" &&
                            typeof currentStateData.new_clip_data.campaign_start_date !== "undefined"
                          ) {
                            return minSelectableDateForClip;
                          } else {
                            return standardFormatToday();
                          }
                        })()}
                        maxSelectable={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                          typeof currentStateData.new_clip_data.campaign_end_date !== "undefined" &&
                          formatToStandardDate(currentStateData.new_clip_data.campaign_end_date)
                        }
                        fieldError={
                          typeof fieldErrors.clip_end_date !== "undefined" && fieldErrors.clip_end_date !== ""
                            ? fieldErrors.clip_end_date
                            : ""
                        }
                        onChangeCall={(selectedDate) => {
                          if (currentStateData?.new_clip_data?.clip_unlimited === false && usedFor === "update") {
                            setEndDateConfirmation(true);
                          } else {
                            setEndDateConfirmation(false);
                          }

                          const selectedData = {
                            clip_end_date: selectedDate,
                          };

                          if (isCloneMode) {
                            // On the first date interaction in clone mode, clear all scheduling fields;
                            // on subsequent end date changes, only calendar weeks are cleared
                            if (!cloneDateChangedRef.current) {
                              cloneDateChangedRef.current = true;
                              selectedData.clip_weeks = [];
                              selectedData.clip_week_days = [];
                              selectedData.clip_week_day_options = [];
                              selectedData.hours = [];
                            } else {
                              selectedData.clip_weeks = [];
                            }
                          }

                          handleFieldChange(selectedData);
                          setTimeout(() => {
                            calculateWeeks({ ...currentStateData.new_clip_data, clip_end_date: selectedDate });
                          }, 1000);
                        }}
                        onWeekSelect={handleWeekSelect}
                        weekSelectionMinDate={weekSelectionMin}
                        weekSelectionMaxDate={weekSelectionMax}
                      />
                    </Stack>
                    {(showStartDateConfirmation || showEndDateConfirmation) && (
                      <>
                        {showStartDateConfirmation && (
                          <>
                            <Box height={20} />
                            <StyledInfoBox>
                              <Typography variant="body2">
                                This visual is scheduled to start today. If the store is already open, the visual will begin playing within the next hour. Be aware that data for today might be incomplete, affecting later reports.
                              </Typography>
                            </StyledInfoBox>
                          </>
                        )}

                        {showEndDateConfirmation && (
                          <>
                            <Box height={10} />
                            <StyledInfoBox>
                              <Typography variant="body2">
                                When adjusting the dates of an existing visual, please note that the impression target is not updated automatically.
                                Remember to manually adjust the impression target for this visual if needed.
                              </Typography>
                            </StyledInfoBox>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}

              {/* Same gap whether or not the start-today info box is showing, so the box is not pushed against Volume. */}
              <Box height={30} />
              <Stack direction={"row"} spacing={4} alignItems="flex-end">
                {/* --------- volume -------- */}
                <Box flex={1}>
                  <TextInputField
                    fieldRequired={true}
                    elemId="volume"
                    label="Volume"
                    fieldName="volume"
                    readOnlyFlag={statusBasedReadOnly}
                    showlabel={true}
                    sx={{ mt: 0, mb: 0 }}
                    defaultVal={
                      typeof currentStateData.new_clip_data !== "undefined" &&
                        typeof currentStateData.new_clip_data.clip_volume !== "undefined"
                        ? currentStateData.new_clip_data.clip_volume
                        : ""
                    }
                    inputType={"text"}
                    fieldError={
                      typeof fieldErrors.clip_volume !== "undefined" && fieldErrors.clip_volume !== ""
                        ? fieldErrors.clip_volume
                        : ""
                    }
                    onChangeCall={(e) => {
                      e.preventDefault();
                      const selectedData = {
                        clip_volume: e.target.value,
                      };
                      handleFieldChange(selectedData);
                    }}
                    iconProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ marginRight: "15px" }}>
                          <HtmlTooltip
                            title={
                              <Box width={300}>
                                <Typography variant="body2">
                                  <strong>Volume Ranges:</strong> <br />
                                  <Box height={10} />
                                  <strong>{"> 200:"}</strong> Very loud, usually deterioration in sound quality <br />
                                  <Box height={5} />
                                  <strong>170 - 200: </strong> Normal loudness, usually high sound quality (recommended range) <br />
                                  <Box height={5} />
                                  <strong>{"< 170:"} </strong> Very quiet, usually too quiet for store environment <br />
                                </Typography>
                              </Box>
                            }
                          >
                            <IconButton>
                              <Info aria-label="volumeToolTip" />
                            </IconButton>
                          </HtmlTooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* ---------- stores selection ---------- */}
                {typeof currentStateData.stores_list_for_visuals !== "undefined" && (
                  <Box flex={1}>
                    <ClipStoresField
                      options={dropdownStoreOptions}
                      selectedOptions={currentStateData.selected_store_options || []}
                      readOnlyFlag={statusBasedReadOnly}
                      onChangeCall={applyStoreSelection}
                      onBlurCall={refreshAvailabilityFromStores}
                      onOpenDetail={() => setIsDetailSelectionModalOpen(true)}
                      fieldError={
                        typeof fieldErrors.clip_stores !== "undefined" && fieldErrors.clip_stores !== ""
                          ? fieldErrors.clip_stores
                          : ""
                      }
                    />
                  </Box>
                )}
              </Stack>
              {/* ----------- unlimited toggle --------- */}
              <Box height={14} />
              <Stack direction={"row"} spacing={4} sx={{ ml: -0.9 }}>
                {/* -------- unlimited ------- */}
                <HtmlTooltip
                  title={
                    currentStateData.new_clip_data?.clip_optimized
                      ? "Unlimited cannot be enabled while Optimized is active"
                      : ""
                  }
                  disableHoverListener={!currentStateData.new_clip_data?.clip_optimized}
                >
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <StyledSwitch
                        disabled={
                          currentStateData.new_clip_data?.clip_optimized || statusBasedReadOnly
                        }
                        checked={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                            typeof currentStateData.new_clip_data.clip_unlimited !== "undefined"
                            ? currentStateData.new_clip_data.clip_unlimited
                            : false
                        }
                        onChange={(e) => {
                          const selectedData = {
                            clip_unlimited: e.target.checked,
                          };
                          handleFieldChange(selectedData);
                        }}
                      />
                    }
                    label="Unlimited"
                  />
                </HtmlTooltip>

                {/* -------- optimized ------- */}
                <HtmlTooltip
                  title={
                    currentStateData.new_clip_data?.clip_unlimited
                      ? "Optimized cannot be enabled while Unlimited is active"
                      : (!currentStateData.selected_store_options || currentStateData.selected_store_options.length === 0)
                        ? "Select stores to enable this option"
                        : ""
                  }
                  disableHoverListener={
                    !currentStateData.new_clip_data?.clip_unlimited &&
                    currentStateData.selected_store_options?.length > 0
                  }
                >
                  <FormControlLabel
                    control={
                      <StyledSwitch
                        disabled={
                          currentStateData.new_clip_data?.clip_unlimited ||
                          statusBasedReadOnly ||
                          !currentStateData.selected_store_options ||
                          currentStateData.selected_store_options.length === 0
                        }
                        checked={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                            typeof currentStateData.new_clip_data.clip_optimized !== "undefined"
                            ? currentStateData.new_clip_data.clip_optimized
                            : false
                        }
                        onChange={(e) => {
                          const isEnabling = e.target.checked;
                          const selectedData = {
                            clip_optimized: isEnabling,
                          };
                          if (isEnabling) {
                            selectedData.clip_weeks = [];
                            selectedData.clip_week_days = [];
                            selectedData.clip_week_day_options = [];
                            selectedData.hours = [];
                            lastFetchedWeeksKeyRef.current = "";
                            lastFetchedWeekdaysKeyRef.current = "";
                            setCommittedWeeksKey("");
                            setCommittedWeekdaysKey("");
                            setWeekdaysAvailabilityReady(false);
                            setHoursAvailabilityReady(false);
                            setWeekdayOptions([]);
                            setHoursOptions([]);
                          }
                          handleFieldChange(selectedData);
                        }}
                      />
                    }
                    label="Optimized"
                  />
                </HtmlTooltip>
              </Stack>

              {/* --------- weeks, days and hour selection ---------- */}

              {(typeof currentStateData.new_clip_data !== "undefined" &&
                typeof currentStateData.new_clip_data.clip_unlimited !== "undefined" &&
                currentStateData.new_clip_data.clip_unlimited === false) &&
                (typeof currentStateData.new_clip_data !== "undefined" &&
                  typeof currentStateData.new_clip_data.clip_optimized !== "undefined" &&
                  currentStateData.new_clip_data.clip_optimized) ? (
                <>
                  <Box height={10} />
                  <Stack direction={"row"} spacing={4} alignItems="flex-end">
                    {/* -------- weeks numbers ------- */}
                    <HtmlTooltip
                      title={datesNotSelected ? "Select dates to enable this option" : ""}
                      disableHoverListener={!datesNotSelected}
                    >
                      <Box sx={{ width: '100%' }}>
                        <MultiSelect
                          label="Calendar Weeks *"
                          elemId="clipWeekNum"
                          enableFullWidth={true}
                          sortOptions={false}
                          allowDuplicates
                          readOnlyFlag={statusBasedReadOnly || datesNotSelected}
                          options={
                            typeof currentStateData.campaign_weeks !== "undefined" ? currentStateData.campaign_weeks : []
                          }
                          defaultVal={
                            typeof currentStateData.new_clip_data !== "undefined" &&
                              typeof currentStateData.new_clip_data.clip_weeks !== "undefined"
                              ? typeof currentStateData.campaign_weeks !== "undefined"
                                ? currentStateData.campaign_weeks.filter((weekInfo) => {
                                  return currentStateData.new_clip_data.clip_weeks.includes(weekInfo.value);
                                })
                                : []
                              : []
                          }
                          selectionCheck={(option, value) => option.value === value.value}
                          onBlurCall={refreshAvailabilityFromWeeks}
                          onFocusCall={() => clearFieldError("clip_weeks")}
                          onChangeCall={(selectedOption) => {
                            let selectedWeekNum = [];
                            // selectedOption.forEach((option) => {
                            //   selectedWeekNum.push(option.value);
                            // });

                            selectedOption.forEach((chosen) => {
                              if (chosen.value !== "all") {
                                selectedWeekNum.push(chosen.value);
                              } else {
                                currentStateData.campaign_weeks.forEach((week) => {
                                  if (week.value !== "all") {
                                    selectedWeekNum.push(week.value);
                                  }
                                });
                              }
                            });

                            const selectedData = {
                              clip_weeks: selectedWeekNum,
                              ...(isCloneMode && {
                                clip_week_days: [],
                                clip_week_day_options: [],
                                hours: [],
                              }),
                            };
                            handleFieldChange(selectedData);
                          }}
                          fieldError={
                            typeof fieldErrors.clip_weeks !== "undefined" && fieldErrors.clip_weeks !== ""
                              ? fieldErrors.clip_weeks
                              : ""
                          }
                        />
                      </Box>
                    </HtmlTooltip>

                    {/* --------- week days -------- */}
                    <HtmlTooltip
                      title={weekdayFieldTooltipTitle}
                      disableFocusListener
                      disableTouchListener
                      disableHoverListener={!weekdayFieldTooltipTitle}
                      open={
                        Boolean(weekdayFieldTooltipTitle) &&
                        isWeekdayFieldTooltipOpen &&
                        hoveredDetailIcon !== "weekday"
                      }
                      onOpen={() => {
                        if (weekdayFieldTooltipTitle) {
                          setIsWeekdayFieldTooltipOpen(true);
                        }
                      }}
                      onClose={() => setIsWeekdayFieldTooltipOpen(false)}
                    >
                      <Box sx={{ width: '100%' }}>
                        <MultiSelect
                          label="Weekdays *"
                          elemId="clipWeekDays"
                          readOnlyFlag={weekdaysFieldLocked}
                          enableFullWidth={true}
                          sortOptions={false}
                          allowDuplicates
                          disableSearch={true}
                          options={weekdaysReadyForInput ? combinedWeekdayOptions : []}
                          disabledOptions={isWeekdayUnavailable}
                          tooltipTitleForDisabledOptions="No Store Open"
                          defaultVal={
                            typeof currentStateData.new_clip_data !== "undefined" &&
                              typeof currentStateData.new_clip_data.clip_week_day_options !== "undefined"
                              ? // ? currentStateData.new_clip_data.clip_week_day_options
                              currentStateData.new_clip_data.clip_week_day_options
                              : []
                          }
                          selectionCheck={(option, value) => option.value === value.value}
                          onBlurCall={refreshAvailabilityFromWeekdays}
                          onFocusCall={() => clearFieldError("clip_week_days")}
                          onChangeCall={(selectedOption) => {
                            const selectableDays = getCombinedWeekdayOptions().filter(
                              (d) => d.value !== "all" && !isWeekdayUnavailable(d)
                            );
                            let selectedDays = [];
                            let selectedWeekDays = [];
                            const hasAll = selectedOption.some((day) => day.value === "all");
                            if (hasAll) {
                              selectedDays = selectableDays.map((d) => d.label);
                              selectedWeekDays = selectableDays;
                            } else {
                              selectedWeekDays = selectedOption.filter((d) => d.value !== "all");
                              selectedDays = selectedWeekDays.map((d) => d.label);
                            }

                            const selectedData = {
                              clip_week_days: selectedDays,
                              clip_week_day_options: selectedWeekDays,
                            };
                            handleFieldChange(selectedData);
                          }}
                          fieldError={
                            typeof fieldErrors.clip_week_days !== "undefined" && fieldErrors.clip_week_days !== ""
                              ? fieldErrors.clip_week_days
                              : ""
                          }
                          additionalIconWithTooltip={true}
                          additionalIconMarkup={(setOpen) => {
                            return renderDetailSelectionIcon("weekday", (e) => {
                              e.stopPropagation();
                              if (weekdaysFieldLocked) {
                                return;
                              }
                              setHoveredDetailIcon(null);
                              setOpen(false);
                              clearFieldError("clip_week_days");
                              setIsDetailedWeekdayModalOpen(true);
                            });
                          }}
                        />
                      </Box>
                    </HtmlTooltip>
                  </Stack>
                  <Box height={28} />
                  <Stack direction={"row"} spacing={4} alignItems="flex-end">
                    <HtmlTooltip
                      title={hourFieldTooltipTitle}
                      disableFocusListener
                      disableTouchListener
                      disableHoverListener={!hourFieldTooltipTitle}
                      open={
                        Boolean(hourFieldTooltipTitle) &&
                        isHourFieldTooltipOpen &&
                        hoveredDetailIcon !== "hours"
                      }
                      onOpen={() => {
                        if (hourFieldTooltipTitle) {
                          setIsHourFieldTooltipOpen(true);
                        }
                      }}
                      onClose={() => setIsHourFieldTooltipOpen(false)}
                    >
                      <Box sx={{ width: '100%' }}>
                        <MultiSelect
                          label="Hours of Day *"
                          elemId={"hoursOfDay"}
                          sortOptions={false}
                          allowDuplicates
                          options={hoursReadyForInput ? hoursOptions : []}
                          disabledOptions={(option) => option.disabled === true}
                          tooltipTitleForDisabledOptions="No Store Open"
                          defaultVal={
                            currentStateData?.new_clip_data?.hours
                              ? hoursOptions.filter(o => currentStateData.new_clip_data.hours.includes(o.value))
                              : []
                          }
                          selectionCheck={(option, value) => option.value === value.value}
                          enableFullWidth={true}
                          readOnlyFlag={hoursFieldLocked}
                          onFocusCall={() => clearFieldError("hours")}
                          onChangeCall={(selectedOption) => {
                            const hasAll = selectedOption.some((o) => o.value === "all");
                            const selectableHours = hoursOptions.filter((o) => o.value !== "all" && !o.disabled);
                            const selectedHours = hasAll
                              ? selectableHours.map((o) => o.value)
                              : selectedOption.filter((o) => o.value !== "all" && !o.disabled).map((o) => o.value);
                            handleFieldChange({ hours: selectedHours });
                          }}
                          fieldError={
                            typeof fieldErrors.hours !== "undefined" && fieldErrors.hours !== ""
                              ? fieldErrors.hours
                              : ""
                          }
                          additionalIconWithTooltip={true}
                          additionalIconMarkup={(setOpen) => {
                            return renderDetailSelectionIcon("hours", (e) => {
                              e.stopPropagation();
                              if (hoursFieldLocked) {
                                return;
                              }
                              setHoveredDetailIcon(null);
                              setOpen(false);
                              clearFieldError("hours");
                              setIsDetailedHourModalOpen(true);
                            });
                          }}
                        />
                      </Box>
                    </HtmlTooltip>
                    <Box sx={{ width: "100%" }}>
                      <TextInputField
                        fieldRequired={true}
                        elemId="campaignContacts"
                        label="Impressions"
                        fieldName="campaign_contacts"
                        readOnlyFlag={statusBasedReadOnly}
                        showlabel={true}
                        inputHeight={21}
                        sx={{ mt: 0, mb: 0 }}
                        defaultVal={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                            typeof currentStateData.new_clip_data.clip_contacts !== "undefined"
                            ? currentStateData.new_clip_data.clip_contacts
                            : ""
                        }
                        inputType={"text"}
                        fieldError={
                          typeof fieldErrors.clip_contacts !== "undefined" && fieldErrors.clip_contacts !== ""
                            ? fieldErrors.clip_contacts
                            : ""
                        }
                        onChangeCall={(e) => {
                          e.preventDefault();
                          const selectedData = {
                            clip_contacts: e.target.value,
                          };
                          handleFieldChange(selectedData);
                        }}
                      />
                    </Box>
                  </Stack>
                </>
              ) :
                (typeof currentStateData.new_clip_data !== "undefined" &&
                  typeof currentStateData.new_clip_data.clip_unlimited !== "undefined" &&
                  !currentStateData.new_clip_data.clip_unlimited && (
                    <>
                      <Box height={10} />
                      <TextInputField
                        fieldRequired={true}
                        elemId="campaignContacts"
                        label="Impressions"
                        fieldName="campaign_contacts"
                        readOnlyFlag={statusBasedReadOnly}
                        showlabel={true}
                        inputHeight={21}
                        sx={{ mt: 0, mb: 0 }}
                        defaultVal={
                          typeof currentStateData.new_clip_data !== "undefined" &&
                            typeof currentStateData.new_clip_data.clip_contacts !== "undefined"
                            ? currentStateData.new_clip_data.clip_contacts
                            : ""
                        }
                        inputType={"text"}
                        fieldError={
                          typeof fieldErrors.clip_contacts !== "undefined" && fieldErrors.clip_contacts !== ""
                            ? fieldErrors.clip_contacts
                            : ""
                        }
                        onChangeCall={(e) => {
                          e.preventDefault();
                          const selectedData = {
                            clip_contacts: e.target.value,
                          };
                          handleFieldChange(selectedData);
                        }}
                      />
                    </>
                  ))}

              <Box height={40} />
              {/* ----------- last updated --------- */}
              <Stack direction={"row"} spacing={4}>
                {usedFor === "update" && (
                  <StyledDarkRedInformationTypography variant="body2">
                    Last Modified:
                    <StyledInformationSpan
                      style={{
                        color: "#2D2D37",
                        fontWeight: "bold",
                        marginLeft: "5px",
                      }}
                    >
                      {clipInfo.last_edit_by}
                    </StyledInformationSpan>
                  </StyledDarkRedInformationTypography>
                )}
              </Stack>
            </Box>
          </StyledMainBox>
        </DialogContent >
        <DialogActions>
          <Stack direction={"row"} spacing={2}>
            <CloseButton
              onClick={(e) => {
                e.preventDefault();
                closeModal();
              }}
              label="Cancel"
              icon={<Close />}
            />
            {/* save/update button is only visible for adding a new clip or while updating active or scheduled clip */}
            {usedFor === "add" ||
              (typeof currentStateData.new_clip_data !== "undefined" &&
                typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
                ["Active", "Scheduled"].includes(currentStateData.new_clip_data.clip_start_end_status)) ? (
              <ActionButton
                onClick={(e) => {
                  e.preventDefault();
                  setFieldErrors({});
                  refreshAvailabilityFromStores();
                  refreshAvailabilityFromWeeks();
                  refreshAvailabilityFromWeekdays();
                  saveUpdpateClipInfo(true, true);
                }}
                label={usedFor === "update" ? "Update" : "Save"}
                icon={<Save />}
              />
            ) : (
              <></>
            )}
          </Stack>
        </DialogActions>
      </Dialog >
      {/* showPlayoutStopConfirmation
       */}
      {/* ------- stop playout confirmation -------- */}
      {
        showPlayoutStopConfirmation === true && (
          <Confirmation
            showConfirmationPopup={showPlayoutStopConfirmation}
            closePopup={(e) => {
              setShowPlayoutStopConfirmation(false);
            }}
            confirmationActionCall={() => {
              handleFieldChange({
                active: false, clip_end_date: standardFormatToday(),
              })
              saveUpdpateClipInfo(true, false);
            }}
            confirmationMessage={
              <>
                <Typography variant="body2">
                  Stopping the playout will immediately halt the distribution of this visual in all stores. No further campaign data will be collected, and the visual's end date will be set to today.
                </Typography>
                <Box height={10} />
                <Typography variant="body2">
                  Once confirmed, the visual will stop playing in all stores within approximately 15 minutes. You may restart the playout at any time before midnight. Afterwards, the visual expires permanently and cannot be resumed.
                </Typography>
                <Box height={10} />
                <Typography variant="body2">Do you want to proceed?</Typography>
              </>
            }
            confirmationTitle={"Stop Playout"}
          />
        )
      }

      {/* ------ continue playout confirmation ------ */}
      {
        showContinuePlayoutConfirmation === true && (
          <Confirmation
            showConfirmationPopup={showContinuePlayoutConfirmation}
            closePopup={(e) => {
              setShowContinuePlayoutConfirmation(false);
            }}
            confirmationActionCall={() => {
              handleFieldChange({
                active: true,
              })
              saveUpdpateClipInfo(false, false);
            }}
            confirmationMessage={
              <>
                <Typography variant="body2">
                  Continuing the playout will distribute the visual to all selected stores. Please remember to set a new end date.
                </Typography>
                <Box height={10} />
                <Typography variant="body2">
                  Once confirmed, the visual will begin playing in all stores within approximately 15 minutes.
                </Typography>
                <Box height={10} />
                <Typography variant="body2">Do you want to proceed?</Typography>
              </>
            }
            confirmationTitle={"Continue Playout"}
          />
        )
      }
      {
        isDetailSelectionModalOpen && (
          <StoreDetailTable
            open={isDetailSelectionModalOpen}
            onClose={() => {
              setIsDetailSelectionModalOpen(false);
              refreshAvailabilityFromStores();
              dispatchAction(deleteItems(["store_selection_pagination", "store_list_filter_applied", "detailed_stores_filtered"]));
            }}
            applyChangesOnSelect={false}
            selectedValues={
              typeof currentStateData.selected_store_options !== "undefined"
                ? currentStateData.selected_store_options.map((storeRecord) => storeRecord.id)
                : []
            }
            storeOptionsList={modalStoreOptions}
            toHandleApplyChanges={(selectedStoreIds) => {
              const selectedOptions = dropdownStoreOptions.filter((option) =>
                selectedStoreIds.includes(option.id)
              );
              applyStoreSelection(selectedOptions);
            }}
            title="Add Contact-based Visual: Detailed Selection"
            disableRestoreFocus={true}
            readOnly={statusBasedReadOnly}
            enableExcelDownload={true}
            excelExportScope="selected"
            excelFileName={(() => {
              const currentDate = checkTodayDateWithFormat("YYYYMMDD");
              const clipName = storeSelectionInfo?.name;
              return clipName
                ? `${currentDate}_${clipName.replaceAll(" ", "_")}_StoreOverview.xlsx`
                : `${currentDate}_NoClip_StoreOverview.xlsx`;
            })()}
            disableApplyWhenEmpty={true}
          />
        )
      }

      {/* --------- Detailed Weekday Selection popup --------- */}
      <DetailedWeekdaySelection
        open={isDetailedWeekdayModalOpen}
        onClose={() => {
          setIsDetailedWeekdayModalOpen(false);
          refreshAvailabilityFromWeekdays();
        }}
        usedFor={usedFor}
        availableWeekdays={getCombinedWeekdayOptions()
          .filter((o) => o.value !== "all")
          .map((o) => ({ ...o, disabled: isWeekdayUnavailable(o) }))}
      />

      {/* --------- Detailed Hour Selection popup --------- */}
      <DetailedHourSelection
        open={isDetailedHourModalOpen}
        onClose={() => setIsDetailedHourModalOpen(false)}
        usedFor={usedFor}
        availableHours={hoursOptions.filter((o) => o.value !== "all")}
      />
    </>
  );
};

export default AddNewClip;
